/**
 * TanStack Query hooks for Jerry API.
 *
 * All hooks delegate to the plain API functions in ./index.ts so the raw
 * fetch layer stays usable outside of React. Import hooks from
 * `@joint/jerry/api/queries` or the barrel re-export.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App } from "antd";
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import type { AiVerdict } from "../types/aiVerdict";
import type { CapabilityRun } from "../types/capabilityRun";

import { downloadAiReviewExport, downloadDocument, downloadDraftDocExport, downloadDraftDocExportRedline, downloadExtractionJobExport, downloadRedactedDocument, DownloadError } from "../utils/download";
import type { AiReviewExportFormat } from "../utils/download";
import { AUTH_TOKEN_KEY } from "../constants/auth";
import {
  DECISION_STATUS,
  CHANGE_STATUS,
  COMPARISON_STATUS,
} from "../constants/veridoc";
import { RAG_INDEX_STATUS } from "../constants/document-types";
import { meetingAiPollInterval } from "../utils/meetingAiPolling";
import {
  clientsApi,
  mattersApi,
  tasksApi,
  documentsApi,
  conversationsApi,
  advisorsApi,
  clientServiceTeamApi,
  dashboardApi,
  usersApi,
  lawyersApi,
  deadlinesApi,
  timeEntriesApi,
  actionTypesApi,
  staffProfilesApi,
  resourcesApi,
  auditLogsApi,
  authApi,
  staffApi,
  gradesApi,
  departmentsApi,
  positionsApi,
  rolesApi,
  userRolesApi,
  capabilitiesApi,
  customAdvisorsApi,
  mcpServersApi,
  connectorsApi,
  promptTemplatesApi,
  promptOptimizationApi,
  knowledgeApi,
  knowledgeCategoriesApi,
  externalLinksApi,
  pipelineExecutionsApi,
  pipelineTemplatesApi,
  pipelineInstancesApi,
  veridocApi,
  aiReviewApi,
  conflictChecksApi,
  permissionsApi,
  memoryApi,
  llmSettingsApi,
  draftsApi,
  draftDocsApi,
  dueDiligenceProjectsApi,
  meetingsApi,
  workbenchApi,
  calendarApi,
  redactApi,
  scheduledTasksApi,
  ipoVaultApi,
  isApiError,
  extractionJobsApi,
  aiReviewBatchesApi,
  aiVerdictsApi,
} from "./index";
import { vaultQaApi } from "./ipoVaultQa";
import type {
  VaultQaRunRecord,
  VaultQaSessionSummary,
} from "../types/ipoVaultQa";
import type {
  PipelineInstanceCreatePayload,
  PipelineInstanceUpdatePayload,
  ExecutorConfigPayload,
  LLMSettingsUpdatePayload,
} from "./index";
import type { Capability, CapabilityCreateRequest, CapabilityUpdateRequest } from "../types/capability";
import type {
  CustomAdvisorCreateRequest,
  CustomAdvisorUpdateRequest,
} from "../types/customAdvisor";
import type { MemoryCreateRequest, MemoryUpdateRequest } from "../types/memory";
import type { AiFindingProcessed, AiVerdictReasonCode } from "../types/aiReview";
import type {
  McpServer,
  McpServerRegisterRequest,
  McpServerSyncResponse,
  McpBindingPatch,
  McpServerTool,
} from "../types/mcp-server";
import type {
  PaginatedResponse,
  Message,
  EmployeeGrade,
  Department,
  Position,
  Role,
  UserCreatePayload,
  UserUpdatePayload,
  StaffProfileCreatePayload,
  StaffProfileUpdatePayload,
  DepartmentMemberAddPayload,
  TimeEntryCreatePayload,
  TimeEntryUpdatePayload,
  WorkbenchViewConfig,
  ExtractionJobCreateRequest,
} from "../types";
import type { Calendar } from "../types/calendar";
import type {
  KnowledgeItemCreateRequest,
  KnowledgeItemUpdateRequest,
  KnowledgeCategoryCreateRequest,
  KnowledgeCategoryUpdateRequest,
  KnowledgeCategoryMoveRequest,
} from "../types/knowledge";
import type {
  ComparisonResponse,
  DecisionStatus,
  ChangeStatus,
} from "../types/veridoc";
import type {
  ConflictCheckCreateRequest,
  ConflictCheckUpdateRequest,
  ConflictCheckStatusChangeRequest,
} from "../types/conflict";
import type { DraftCreateRequest, DraftSaveAsDocumentRequest } from "../types/draft";
import type { RedactType } from "../types/redact";
import { DRAFT_NON_TERMINAL_STATUSES } from "../types/draft";
import type {
  DraftDocBodyUpdateRequest,
  DraftDocCreateRequest,
  DraftDocListParams,
  DraftDocSaveAsDocumentRequest,
  DraftDocUpdateRequest,
  DraftProposalBatchDecisionRequest,
  DraftProposalDecisionRequest,
} from "../types/draftDoc";
import type {
  DueDiligenceProjectCreatePayload,
  DueDiligenceProjectListParams,
  DueDiligenceProjectUpdatePayload,
  DueDiligenceSubReportSaveAsDocumentPayload,
  PublicResearchSourcesParams,
} from "../types/dueDiligence";
import type {
  MeetingAiAction,
  MeetingBookPayload,
  MeetingContentImportRequest,
  MeetingCreateRequest,
  MeetingListParams,
  MeetingSource,
  MeetingStatus,
  MeetingTaskCandidateDecision,
  MeetingTranscriptImportRequest,
  MeetingTranscriptSegmentsEditRequest,
  MeetingTranscriptSpeakerRenameRequest,
  MeetingUpdateRequest,
} from "../types/meeting";
import type {
  ScheduledTaskCreatePayload,
  ScheduledTaskUpdatePayload,
  ScheduledTaskStatus,
  ScheduledTaskTargetType,
} from "../types/scheduledTask";
import type { AiReviewBatch } from "../types/aiReviewBatch";
import type { AiVerdictCreatePayload } from "../types/aiVerdict";
import type {
  Overview,
  LegalIssue,
  LegalIssueFacets,
  CsrcFiling,
  CsrcFeedback,
  CsrcFeedbackFacets,
  TimelineSnapshot,
  HkexCase,
  HkexAnnouncement,
  SecFiling,
  SyncRun,
  SyncTriggerResponse,
  LegalIssuesParams,
  CsrcFilingsParams,
  CsrcFeedbackParams,
  HkexCasesParams,
  HkexAnnouncementsParams,
  SecFilingsParams,
  SyncRunsParams,
} from "../types/ipoVault";

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const queryKeys = {
  compass: ["compass"] as const,
  matters: (params?: Record<string, unknown>) => ["matters", params] as const,
  matter: (id: string) => ["matters", id] as const,
  tasks: (params?: Record<string, unknown>) => ["tasks", params] as const,
  myTasks: (params?: Record<string, unknown>) => ["my-tasks", params] as const,
  clients: (params?: Record<string, unknown>) => ["clients", params] as const,
  client: (id: string) => ["clients", id] as const,
  documents: (params?: Record<string, unknown>) =>
    ["documents", params] as const,
  /** Version chain of a single document (US-C02). */
  documentVersions: (id: string) => ["documents", id, "versions"] as const,
  conversations: (params?: Record<string, unknown>) =>
    ["conversations", params] as const,
  users: (params?: Record<string, unknown>) =>
    ["users", params] as const,
  deadlines: (params?: Record<string, unknown>) =>
    ["deadlines", params] as const,
  timeEntries: (params?: Record<string, unknown>) =>
    ["time-entries", params] as const,
  timeEntrySummary: (params?: Record<string, unknown>) =>
    ["time-entries-summary", params] as const,
  actionTypes: (params?: Record<string, unknown>) =>
    ["action-types", params] as const,
  staffProfiles: (params?: Record<string, unknown>) =>
    ["staff-profiles", params] as const,
  myStaffProfile: ["staff-profiles", "me"] as const,
  conflictChecks: (params?: Record<string, unknown>) =>
    ["conflict-checks", params] as const,
  conflictCheck: (id: string) => ["conflict-checks", id] as const,
  meetings: (params?: Record<string, unknown>) => ["meetings", params] as const,
  meeting: (id: string) => ["meetings", id] as const,
  scheduledTasks: (params?: Record<string, unknown>) =>
    ["scheduled-tasks", params] as const,
  scheduledTask: (id: string) => ["scheduled-tasks", "detail", id] as const,
  scheduledTaskRuns: (id: string, params?: Record<string, unknown>) =>
    ["scheduled-tasks", "detail", id, "runs", params] as const,
  scheduledTaskSummary: ["scheduled-tasks", "summary"] as const,
  /** 期产物人审视图（波次 2）。 */
  scheduledTaskRunReview: (taskId: string, runId: string) =>
    ["scheduled-tasks", "detail", taskId, "runs", runId, "review"] as const,
  aiReviewBatches: (params?: Record<string, unknown>) =>
    ["ai-review-batches", params] as const,
  aiReviewBatch: (id: string) => ["ai-review-batches", "detail", id] as const,
  /** 统一表态表（POST /ai-verdicts 写入族；读取随 review 载荷返回）。 */
  aiVerdicts: ["ai-verdicts"] as const,
  myPermissions: ["my-permissions"] as const,
  drafts: (matterId: string, params?: Record<string, unknown>) =>
    ["jerry-drafts", matterId, params] as const,
  draft: (id: string) => ["jerry-drafts", "detail", id] as const,
  draftDocs: (params?: Record<string, unknown>) =>
    ["draft-docs", params] as const,
  draftDoc: (id: string) => ["draft-docs", "detail", id] as const,
  draftDocVersions: (id: string, params?: Record<string, unknown>) =>
    ["draft-docs", id, "versions", params] as const,
  draftDocProposals: (id: string) => ["draft-docs", id, "proposals"] as const,
  ddProjects: (params?: Record<string, unknown>) =>
    ["due-diligence", "projects", params] as const,
  ddProject: (id: string) => ["due-diligence", "projects", "detail", id] as const,
  ddSubReports: (projectId: string) =>
    ["due-diligence", "projects", projectId, "sub-reports"] as const,
  ddSubReport: (projectId: string, subId: string) =>
    ["due-diligence", "projects", projectId, "sub-reports", subId] as const,
  ddProjectMessages: (projectId: string) =>
    ["due-diligence", "projects", projectId, "messages"] as const,
  // 模式 B：公开信息调研（与模式 A 键空间独立，互不污染缓存）
  ddPublicResearchSources: (
    projectId: string,
    params?: Record<string, unknown>,
  ) =>
    [
      "due-diligence",
      "projects",
      projectId,
      "public-research",
      "sources",
      params,
    ] as const,
};

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export function useCompass() {
  return useQuery({
    queryKey: queryKeys.compass,
    queryFn: () => dashboardApi.compass().then((res) => res.data),
  });
}

// ---------------------------------------------------------------------------
// Matters
// ---------------------------------------------------------------------------

export function useMatters(
  params?: {
    clientId?: string;
    status?: string;
    /** 事项名称/编号模糊检索关键词（服务端匹配） */
    search?: string;
    page?: number;
    pageSize?: number;
  },
  options?: {
    /** false = 暂不发请求（惰性加载：仅在选择器实际打开时拉取）。 */
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: queryKeys.matters(params as Record<string, unknown> | undefined),
    queryFn: () => mattersApi.list(params),
    enabled: options?.enabled ?? true,
  });
}

export function useMatterDetail(id: string) {
  return useQuery({
    queryKey: queryKeys.matter(id),
    queryFn: () => mattersApi.get(id).then((res) => res.data),
    enabled: !!id,
  });
}

const MATTER_BRIEF_KEY = "matter-brief";

/** 事项背景 (US-G04)。非成员访问事项详情本就被 403 拦在外层，进不到这里
 * （BR-G04-1 无须额外前端门控）。 */
export function useMatterBrief(matterId: string) {
  return useQuery({
    queryKey: [MATTER_BRIEF_KEY, matterId] as const,
    queryFn: () => mattersApi.getBrief(matterId).then((res) => res.data),
    enabled: !!matterId,
  });
}

/**
 * 保存事项背景。成功后直接把返回的最新记录写入缓存（含新 version，供下一次
 * 保存使用），不额外触发 refetch。409/422 等业务错误交由调用方从
 * `mutateAsync` 的 reject 里读取 —— 不同错误码需要不同的 UI 处理（冲突提示 +
 * 保留草稿 / 字数提示），不适合在这里塞一个通用 toast。
 */
export function useUpdateMatterBrief() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      matterId,
      content,
      version,
    }: {
      matterId: string;
      content: string;
      version: number;
    }) => mattersApi.updateBrief(matterId, { content, version }).then((res) => res.data),
    onSuccess: (data, { matterId }) => {
      qc.setQueryData([MATTER_BRIEF_KEY, matterId], data);
    },
  });
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export function useTasks(params?: {
  matterId?: string;
  meetingId?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: queryKeys.tasks(params as Record<string, unknown> | undefined),
    queryFn: () => tasksApi.list(params),
  });
}

export function useTask(taskId: string | null | undefined) {
  return useQuery({
    queryKey: ["jerry", "task", taskId],
    enabled: !!taskId,
    queryFn: () => tasksApi.get(taskId as string),
  });
}

export function useMyTasks(params?: {
  status?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: queryKeys.myTasks(
      params as Record<string, unknown> | undefined,
    ),
    queryFn: () => tasksApi.mine(params),
  });
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export function useClients(params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: queryKeys.clients(params as Record<string, unknown> | undefined),
    queryFn: () => clientsApi.list(params),
  });
}

export function useClientDetail(id: string) {
  return useQuery({
    queryKey: queryKeys.client(id),
    queryFn: () => clientsApi.get(id).then((res) => res.data),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/** How often to refresh a document list that still has documents indexing. */
const RAG_PENDING_POLL_MS = 5000;

/**
 * True when a document-list response contains a document the AI engine is
 * still indexing ("索引中"). Used as a refetchInterval predicate so the badge
 * resolves to 可问答/索引失败 without a manual refresh, and polling stops as
 * soon as every visible document reached a terminal state.
 */
function hasPendingRagDocs(data: unknown): boolean {
  const docs = (data as { data?: { ragIndexStatus?: string | null }[] } | undefined)
    ?.data;
  return Array.isArray(docs)
    ? docs.some((d) => d?.ragIndexStatus === RAG_INDEX_STATUS.PENDING)
    : false;
}

export function useDocuments(params?: {
  matterId?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: queryKeys.documents(
      params as Record<string, unknown> | undefined,
    ),
    queryFn: () => documentsApi.list(params),
    refetchInterval: (query) =>
      hasPendingRagDocs(query.state.data) ? RAG_PENDING_POLL_MS : false,
  });
}

/** Cross-matter document list for the workbench (Document Center).
 *
 * US-D01~D06 (2026-07-04): Extended to support matter tree grouping, multi-dim
 * filters (tags, docTypes, actionStatuses, formats, date range), full-text
 * search, and saved view application. Old single-dim filters (sourceType /
 * search) remain backward compatible — backend ignores new params if absent.
 */
export function useWorkbenchDocuments(params?: {
  // Legacy filters (backward compat)
  sourceType?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  // US-D01: matter scoping (when null/undefined → cross-matter global list)
  matterId?: string;
  groupByMatter?: boolean;
  // US-D03/D04: tag multi-select (slugs)
  tagSlugs?: string[];
  tagOp?: "and" | "or";
  // US-D02/D04: type / status / format multi-select
  docTypes?: string[];
  actionStatuses?: string[];
  formats?: string[];
  // US-D04: date range
  dateField?: "created" | "updated";
  dateFrom?: string;
  dateTo?: string;
  // US-D06: saved view to apply (server expands to filters)
  viewId?: string;
}) {
  return useQuery({
    queryKey: ["workbench-documents", params],
    queryFn: () => documentsApi.list(params),
    refetchInterval: (query) =>
      hasPendingRagDocs(query.state.data) ? RAG_PENDING_POLL_MS : false,
  });
}

/** US-D01: Matters tree for left sidebar (client → matter, with doc counts). */
export function useMattersTree() {
  return useQuery({
    queryKey: ["workbench-matters-tree"],
    queryFn: () => workbenchApi.mattersTree(),
    staleTime: 60_000,
  });
}

/** US-D03: Tag dictionary (system presets + tenant customs). */
export function useTags(search?: string) {
  return useQuery({
    queryKey: ["tags", search],
    queryFn: () => workbenchApi.listTags(search),
    staleTime: 120_000,
  });
}

/** US-D03: Update tags on a document (add/remove, source='user'). */
export function useUpdateDocumentTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { documentId: string; tagSlugs: string[] }) =>
      workbenchApi.updateDocumentTags(args.documentId, args.tagSlugs),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["workbench-documents"] });
      qc.invalidateQueries({ queryKey: ["document", vars.documentId] });
    },
  });
}

/** US-D06: List saved workbench views for the current user. */
export function useWorkbenchViews() {
  return useQuery({
    queryKey: ["workbench-views"],
    queryFn: () => workbenchApi.listViews(),
    staleTime: 60_000,
  });
}

/** US-D06: Save current filter/column config as a new view. */
export function useSaveWorkbenchView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      name: string;
      config: WorkbenchViewConfig;
      isDefault?: boolean;
    }) => workbenchApi.createView(args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workbench-views"] });
    },
  });
}

/** US-D06: Update a saved view (rename / replace config / toggle default). */
export function useUpdateWorkbenchView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      viewId: string;
      name?: string;
      config?: WorkbenchViewConfig;
      isDefault?: boolean;
    }) => workbenchApi.updateView(args.viewId, args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workbench-views"] });
    },
  });
}

/** US-D06: Delete a saved view. */
export function useDeleteWorkbenchView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (viewId: string) => workbenchApi.deleteView(viewId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workbench-views"] });
    },
  });
}

/** US-D07 (reserved): Batch action on multiple documents. */
export function useBatchActionDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      ids: string[];
      action: string;
      payload?: Record<string, unknown>;
    }) =>
      // The /documents/batch endpoint is reserved (US-D07) and not yet shipped
      // on the backend; routing through the shared helper ensures a clean 404
      // ApiError instead of an opaque fetch failure once it lands.
      workbenchApi.batchDocuments(args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workbench-documents"] });
    },
  });
}

/**
 * D3: rebuild a document's RAG index. Recovery path for DEGRADED (was READY but
 * engine-side embedding lost) and FAILED docs. On success the doc's status
 * flips to PENDING and a background job drives it back to READY/FAILED.
 */
export function useReindexDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => documentsApi.reindex(documentId),
    onSuccess: (_data, documentId) => {
      qc.invalidateQueries({ queryKey: ["workbench-documents"] });
      qc.invalidateQueries({ queryKey: ["document", documentId] });
    },
  });
}

/** Single document detail. */
export function useDocument(id: string | null) {
  return useQuery({
    queryKey: ["document", id],
    queryFn: () => documentsApi.get(id!).then((res) => res.data),
    enabled: !!id,
  });
}

/**
 * 按 AI 引擎的文档标识取文档——「定位原文」的前置反查。
 *
 * 引用载荷里的文档 id 由引擎分配，与主库文档 id 不是同一套；直接拿它去请求
 * `/documents/{id}` 恒为 404。查询结果按引擎标识缓存，同一份文档的多条引用
 * 只反查一次。
 */
export function useDocumentByEngineId(engineDocumentId: string | null) {
  return useQuery({
    queryKey: ["document-by-engine-id", engineDocumentId],
    queryFn: () =>
      documentsApi.getByEngineId(engineDocumentId!).then((res) => res.data),
    enabled: !!engineDocumentId,
    retry: false,
  });
}

/**
 * Poll a single conversation-attachment document while it is still indexing, so
 * the composer chip's index badge resolves from 索引中 to 可问答 / 索引失败
 * without a manual refresh. `active` gates the query: pass true only while the
 * attachment is uploaded AND its status is still pending; polling stops the
 * instant the document reaches a terminal index state.
 */
export function useConversationAttachmentStatus(
  documentId: string | undefined,
  active: boolean,
) {
  return useQuery({
    queryKey: ["conversation-attachment-doc", documentId],
    queryFn: () => documentsApi.get(documentId as string).then((res) => res.data),
    enabled: !!documentId && active,
    refetchInterval: (query) =>
      query.state.data?.ragIndexStatus === RAG_INDEX_STATUS.PENDING
        ? RAG_PENDING_POLL_MS
        : false,
  });
}

/**
 * Version chain of a document (US-C02). Returns the full lineage ordered by
 * version desc. Empty/single-element list when the document has no ancestors.
 */
export function useDocumentVersions(documentId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.documentVersions(documentId ?? ""),
    queryFn: () => documentsApi.versions(documentId as string),
    enabled: !!documentId,
  });
}

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

export function useConversations(params?: {
  mode?: string;
  page?: number;
  pageSize?: number;
  status?: "active" | "archived";
}) {
  return useQuery({
    queryKey: queryKeys.conversations(
      params as Record<string, unknown> | undefined,
    ),
    queryFn: () => conversationsApi.list(params),
  });
}

/**
 * Batch 3: conversations filtered by panel anchor. Used by panel-specific
 * conversation lists (workbench review, draft workbench, DD assistants).
 * Shares the ["conversations", params] key family so create/delete mutations
 * automatically invalidate these queries.
 */
export function usePanelConversations(params: {
  anchorType: string;
  anchorId: string | undefined;
  pageSize?: number;
}) {
  const { anchorType, anchorId, pageSize = 20 } = params;
  return useQuery({
    queryKey: queryKeys.conversations({ anchorType, anchorId, pageSize }),
    queryFn: () => conversationsApi.list({ anchorType, anchorId, pageSize }),
    enabled: !!anchorId && anchorId !== "",
  });
}

/**
 * 会话搜索（W2-5）—— HistoryPage 在关键词非空时用它替换 `useConversations`
 * 的数据源。`enabled` 双重把关：`options.enabled`（调用方如需额外挂起，如
 * 防抖尚未落定）与 `q` 非空（避免对空白关键词发起必然 422 的请求）。
 */
export function useSearchConversations(
  params: {
    q: string;
    status?: "active" | "archived";
    page?: number;
    pageSize?: number;
  },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ["conversations-search", params] as const,
    queryFn: () => conversationsApi.search(params),
    enabled: (options?.enabled ?? true) && params.q.trim().length > 0,
  });
}

/** W1-4: rename / archive / restore a conversation (owner-only PATCH). */
export function useUpdateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { title?: string; matterId?: string; status?: "active" | "archived" };
    }) => conversationsApi.update(id, data).then((res) => res.data),
    onSuccess: (_conv, { id }) => {
      void qc.invalidateQueries({ queryKey: ["conversations"] });
      void qc.invalidateQueries({ queryKey: ["conversation", id] });
    },
  });
}

/** W1-4: delete a conversation (hard delete, owner-only; confirm before use). */
export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conversationsApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Matter Members
// ---------------------------------------------------------------------------

export function useMatterMembers(matterId: string) {
  return useQuery({
    queryKey: ["matter-members", matterId] as const,
    queryFn: () => mattersApi.listMembers(matterId).then((res) => res.data),
    enabled: !!matterId,
  });
}

// ---------------------------------------------------------------------------
// Client Service Team
// ---------------------------------------------------------------------------

export function useClientServiceTeam(clientId: string) {
  return useQuery({
    queryKey: ["client-service-team", clientId] as const,
    queryFn: () => clientServiceTeamApi.list(clientId).then((res) => res.data),
    enabled: !!clientId,
  });
}

export function useAddMember(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      mattersApi.addMember(matterId, userId, role).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matter-members", matterId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Matter Document Tree
// ---------------------------------------------------------------------------

export function useMatterTree(matterId: string) {
  return useQuery({
    queryKey: ["matter-tree", matterId] as const,
    queryFn: () => documentsApi.matterTree(matterId).then((res) => res.data),
    enabled: !!matterId,
  });
}

// ---------------------------------------------------------------------------
// Conversation Messages
// ---------------------------------------------------------------------------

/** Query-key prefix shared by the messages list and its optimistic patches. */
export const CONVERSATION_MESSAGES_KEY = "conversation-messages";
/** Server caps page_size at 100; request the max so one page covers most threads. */
const MESSAGES_PAGE_SIZE = 100;

/**
 * Paginated message history for a conversation.
 *
 * Requests newest-first (`order=desc`) so page 1 (`pages[0]`) is always the
 * newest slice; older history is fetched on demand via `fetchNextPage`. The
 * page component flattens `pages[].data` and reverses it to ascending order for
 * display (newest at the bottom).
 */
export function useConversationMessages(conversationId: string | null) {
  return useInfiniteQuery({
    queryKey: [CONVERSATION_MESSAGES_KEY, conversationId] as const,
    queryFn: ({ pageParam }) =>
      conversationsApi.listMessages(conversationId!, {
        page: pageParam,
        pageSize: MESSAGES_PAGE_SIZE,
        order: "desc",
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages
        ? lastPage.meta.page + 1
        : undefined,
    enabled: !!conversationId,
  });
}

export function useConversation(id: string) {
  return useQuery({
    queryKey: ["conversation", id] as const,
    queryFn: () => conversationsApi.get(id).then((res) => res.data),
    enabled: !!id,
  });
}

export const CONVERSATION_DOCUMENTS_KEY = "conversation-documents";

/**
 * Documents a conversation is associated with, grouped by origin (read-only
 * panel). `enabled` gates the fetch so it fires only while the panel is open and
 * the id exists — never issues the request for an unknown conversation. Kept off
 * the messages key so opening the panel never disturbs the thread.
 */
export function useConversationDocuments(
  id: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [CONVERSATION_DOCUMENTS_KEY, id] as const,
    queryFn: () => conversationsApi.listDocuments(id).then((res) => res.data),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

// ---------------------------------------------------------------------------
// Advisors (US-G01/US-G02 "专业顾问" directory)
// ---------------------------------------------------------------------------

// Exported so useCreateCustomAdvisor / useUpdateCustomAdvisor / useDeleteCustomAdvisor
// can invalidate the same key (a custom advisor's create/edit/停用/delete
// changes who GET /advisors returns — the picker and message badges must
// refresh, not just the capability catalog).
export const ADVISORS_KEY = ["advisors"] as const;

/**
 * Visible "专业顾问" directory — shared by the message-level advisor badge
 * (display-name lookup for `Message.answeredByAgent`) and the advisor picker's
 * option list. Same query key everywhere so a panel mount and its picker share
 * one request. 5 分钟 staleTime：顾问目录变化不频繁，选择器每次打开无需重取。
 */
export function useAdvisors() {
  return useQuery({
    queryKey: ADVISORS_KEY,
    queryFn: () => advisorsApi.list().then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateMatter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof mattersApi.create>[0]) =>
      mattersApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matters"] });
      // 数据库页左侧事项树是独立查询，不跟着 ["matters"] 失效。
      // 不一并失效的话，刚在本页建的事项要刷新整页才看得见。
      qc.invalidateQueries({ queryKey: ["workbench-matters-tree"] });
    },
  });
}

export function useUpdateMatter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof mattersApi.update>[1] }) =>
      mattersApi.update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["matters"] });
      qc.invalidateQueries({ queryKey: ["matters", id] });
      qc.invalidateQueries({ queryKey: ["workbench-matters-tree"] });
    },
  });
}

export function useAdvanceStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: string }) =>
      mattersApi.advanceStage(id, stage),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["matters"] });
      qc.invalidateQueries({ queryKey: ["matters", id] });
    },
  });
}

export function useDeleteMatter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mattersApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matters"] });
      qc.invalidateQueries({ queryKey: ["workbench-matters-tree"] });
    },
  });
}

export function useRemoveMember(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => mattersApi.removeMember(matterId, memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matter-members", matterId] });
    },
  });
}

export function useUpdateMemberRole(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { memberId: string; role: string }) =>
      mattersApi.updateMember(matterId, params.memberId, { role: params.role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matter-members", matterId] });
    },
  });
}

export function useTransferLead(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (successorUserId: string) =>
      mattersApi.transferLead(matterId, { successorUserId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matter-members", matterId] });
      qc.invalidateQueries({ queryKey: ["matters", matterId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Matter Stages
// ---------------------------------------------------------------------------

export function useMatterStages(matterId: string) {
  return useQuery({
    queryKey: ["matters", matterId, "stages"] as const,
    queryFn: () => mattersApi.listStages(matterId).then((res) => res.data),
    enabled: !!matterId,
  });
}

export function useActivateStage(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stageId: string) => mattersApi.activateStage(matterId, stageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matters", matterId, "stages"] });
      qc.invalidateQueries({ queryKey: ["matters", matterId] });
    },
  });
}

export function useCompleteStage(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stageId: string) => mattersApi.completeStage(matterId, stageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matters", matterId, "stages"] });
      qc.invalidateQueries({ queryKey: ["matters", matterId] });
    },
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof clientsApi.create>[0]) =>
      clientsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof clientsApi.update>[1] }) =>
      clientsApi.update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["clients", id] });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

export function useChangeTaskStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      waitingFor,
    }: {
      id: string;
      status: string;
      waitingFor?: string;
    }) => tasksApi.changeStatus(id, status, waitingFor),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["my-tasks"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Conversations — mutations
// ---------------------------------------------------------------------------

export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      title?: string;
      matterId?: string;
      taskId?: string;
      mode?: string;
      contextDocumentIds?: string[];
    }) => conversationsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useLinkMatter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      matterId,
    }: {
      conversationId: string;
      matterId: string;
    }) => conversationsApi.linkMatter(conversationId, matterId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

/**
 * Pin (or clear) a conversation's manually-specified "专业顾问" (US-G01
 * replacement path). Writes the returned conversation straight into the
 * `["conversation", id]` cache — the composer's picker label and the pinned
 * chip must flip immediately, not wait on a background refetch. A 404 (the
 * name is not in the caller's visible directory, S1-17) surfaces a specific
 * notice; other failures get a generic retry prompt.
 */
export function useUpdateConversationAdvisor() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      agentName,
    }: {
      conversationId: string;
      agentName: string | null;
    }) => conversationsApi.updateAdvisor(conversationId, agentName).then((res) => res.data),
    onSuccess: (conversation, { conversationId }) => {
      qc.setQueryData(["conversation", conversationId], conversation);
    },
    onError: (err) => {
      message.error(
        isApiError(err) && err.status === 404
          ? "该顾问不可用或不存在，请重新选择"
          : "指定顾问失败，请稍后重试",
      );
    },
  });
}

/**
 * `message-capability-runs` 查询的 key/queryFn/staleTime——{@link
 * useMessageCapabilityRuns}（结果卡渲染路径）与 `useReplyExport` 的导出路径
 * （评审 P2-2：导出时按需 `qc.fetchQuery` 复用同一份缓存约定）共用同一套定义，
 * 不在两处各写一份、缓存缺失时各算各的。结果一旦落库不再变化
 * （capability_run.report 只写一次不更新），staleTime 给得较长。
 */
export function capabilityRunsQueryOptions(conversationId: string, messageId: string) {
  return {
    queryKey: ["message-capability-runs", conversationId, messageId] as const,
    queryFn: () =>
      conversationsApi.listCapabilityRuns(conversationId, messageId).then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  };
}

/**
 * 批 5：一条助手轮上的法律能力执行结果（结果卡数据源）。`enabled` 由调用方
 * 门控——只在该消息的工具轨迹里确实出现过法律能力调用时才发起请求，避免给
 * 每一条助手消息都发一次多半查无结果的查询。
 */
export function useMessageCapabilityRuns(
  conversationId: string,
  messageId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    ...capabilityRunsQueryOptions(conversationId, messageId),
    enabled: (options?.enabled ?? true) && !!conversationId && !!messageId,
  });
}

/**
 * 本人在该会话内的当前表态（表态设计 阶段二）。
 *
 * 整段会话一次取回，按消息 id 建索引供页脚回填——逐条消息各发一次请求会让长
 * 对话开出几十个并发查询。表态可随时改判，因此不设长 staleTime。
 */
export function conversationVerdictsQueryOptions(conversationId: string) {
  return {
    queryKey: ["conversation-verdicts", conversationId] as const,
    queryFn: () => conversationsApi.listVerdicts(conversationId).then((res) => res.data),
  };
}

/** 会话内表态的按消息索引。`enabled` 由调用方门控：起草/尽调面板复用
 *  MessageList 但消息不在 jerry 落库（阶段三另行接入），无表态可查。 */
export function useConversationVerdicts(
  conversationId: string | undefined,
  options?: { enabled?: boolean },
) {
  const query = useQuery({
    ...conversationVerdictsQueryOptions(conversationId ?? ""),
    enabled: (options?.enabled ?? true) && !!conversationId,
  });
  const byMessageId = useMemo(() => {
    const map = new Map<string, AiVerdict>();
    for (const row of query.data ?? []) {
      if (row.targetKind === "conversation_message") map.set(row.targetId, row);
    }
    return map;
  }, [query.data]);
  return { ...query, byMessageId };
}

/**
 * 对一条 AI 回复表态。服务端 append-only，每次调用追加一行（含撤销）。
 *
 * 成功后就地把该消息的表态写进缓存，而不是整表失效重拉：律师补选分类时会连续
 * 触发多次写入，每次都重拉会让刚点上的 chip 在响应回来前先弹回未选中。
 *
 * 失败时回退到重新拉取——本地缓存此刻已与服务端不一致，继续沿用会让界面显示
 * 一个并未落库的表态，那正是表态记录最不该出的错。
 */
export function useCreateMessageVerdict(conversationId: string) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (vars: {
      messageId: string;
      verdict: AiFindingProcessed;
      reasonCode?: AiVerdictReasonCode | null;
      reasonText?: string | null;
      excerpt?: string | null;
    }) =>
      conversationsApi
        .createVerdict(conversationId, vars.messageId, {
          verdict: vars.verdict,
          reasonCode: vars.reasonCode ?? null,
          reasonText: vars.reasonText ?? null,
          excerpt: vars.excerpt ?? null,
        })
        .then((res) => res.data),
    onSuccess: (row) => {
      qc.setQueryData<AiVerdict[]>(
        conversationVerdictsQueryOptions(conversationId).queryKey,
        (prev) => {
          const rest = (prev ?? []).filter(
            (r) => !(r.targetKind === row.targetKind && r.targetId === row.targetId),
          );
          return [...rest, row];
        },
      );
    },
    onError: () => {
      message.error("表态未能保存，请重试");
      void qc.invalidateQueries({
        queryKey: conversationVerdictsQueryOptions(conversationId).queryKey,
      });
    },
  });
}

/**
 * 对一条会话侧法律能力发现表态（表态设计已定结论第 7 条）。
 *
 * 这批发现是 `jerry_ai_finding` 的行，走行内三态；成功后就地更新该消息的
 * 能力执行结果缓存，理由同 {@link useCreateMessageVerdict}。
 */
export function useUpdateCapabilityFindingVerdict(
  conversationId: string,
  messageId: string,
) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (vars: {
      capabilityRunId: string;
      findingId: string;
      processed: AiFindingProcessed;
      reasonCode?: AiVerdictReasonCode | null;
      reasonText?: string | null;
    }) =>
      conversationsApi
        .updateCapabilityFindingVerdict(
          conversationId,
          messageId,
          vars.capabilityRunId,
          vars.findingId,
          {
            processed: vars.processed,
            reasonCode: vars.reasonCode ?? null,
            reasonText: vars.reasonText ?? null,
          },
        )
        .then((res) => res.data),
    onSuccess: (row, vars) => {
      qc.setQueryData<CapabilityRun[]>(
        capabilityRunsQueryOptions(conversationId, messageId).queryKey,
        (prev) =>
          (prev ?? []).map((run) =>
            run.id === vars.capabilityRunId
              ? {
                  ...run,
                  findings: (run.findings ?? []).map((f) => (f.id === row.id ? row : f)),
                }
              : run,
          ),
      );
    },
    onError: () => {
      message.error("表态未能保存，请重试");
      void qc.invalidateQueries({
        queryKey: capabilityRunsQueryOptions(conversationId, messageId).queryKey,
      });
    },
  });
}

/**
 * 「停止本轮」（能力隐式调用架构 §6 决策 2）——只停止内联等待，不取消引擎侧
 * 后台任务。`stopped: false` 时（本轮此刻并未在等待任何异步能力）给出诚实
 * 的提示，不假装已经停止。
 */
export function useStopConversationTurn() {
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (conversationId: string) =>
      conversationsApi.stop(conversationId).then((res) => res.data),
    onSuccess: (result) => {
      message.info(result.stopped ? "已停止等待" : "本轮当前并未在等待任何能力执行");
    },
    onError: () => {
      message.error("停止失败，请稍后重试");
    },
  });
}

/**
 * Apply `updater` to the newest page of the InfiniteData messages cache.
 *
 * The messages query is `order=desc`, so the newest page is `pages[0]` and,
 * within it, the newest turn is index 0. Optimistic bubbles therefore live on
 * the head page; operating only on it keeps the retried-bubble removal and the
 * "failed" flagging scoped to where those bubbles were added. When no page is
 * cached yet, a synthetic newest page is seeded so the optimistic bubble still
 * renders.
 */
function patchNewestMessagesPage(
  qc: ReturnType<typeof useQueryClient>,
  messagesKey: unknown[],
  updater: (data: Message[]) => Message[],
) {
  qc.setQueriesData<InfiniteData<PaginatedResponse<Message>>>(
    { queryKey: messagesKey },
    (old) => {
      if (!old || old.pages.length === 0) {
        return {
          pageParams: [1],
          pages: [
            {
              success: true,
              data: updater([]),
              meta: {
                page: 1,
                pageSize: MESSAGES_PAGE_SIZE,
                total: 0,
                totalPages: 1,
              },
            },
          ],
        };
      }
      const [newest, ...rest] = old.pages;
      return {
        ...old,
        pages: [{ ...newest, data: updater(newest.data) }, ...rest],
      };
    },
  );
}

/**
 * Insert an optimistic user turn at the head of the messages cache and return
 * its client-side temp id. Shared by the sync send mutation and the streaming
 * chat hook so both render the user's bubble the instant a send fires.
 *
 * `replaceId`, when set, is the id of a prior failed bubble being retried — it
 * is removed as the pending bubble is prepended, so a retry does not duplicate
 * the turn. Other undelivered bubbles stay visible.
 *
 * `requestedCapability`, when set, is remembered on the bubble itself (K1
 * 评审高-1) — MessageList's inline 重试 calls `useConversationChat.doSend`
 * directly, bypassing ChatComposer's picker/chip state entirely, so without
 * this the capability selection that produced a failed turn would silently
 * drop on retry.
 */
export function insertOptimisticUserTurn(
  qc: ReturnType<typeof useQueryClient>,
  conversationId: string,
  content: string,
  replaceId?: string,
  requestedCapability?: string,
): string {
  const messagesKey = [CONVERSATION_MESSAGES_KEY, conversationId];
  const tempId = `optimistic-${Date.now()}`;
  const optimistic: Message = {
    id: tempId,
    conversationId,
    role: "user",
    content,
    attachments: null,
    createdAt: new Date().toISOString(),
    sendStatus: "sending",
    requestedCapability: requestedCapability ?? null,
  };
  patchNewestMessagesPage(qc, messagesKey, (data) => [
    optimistic,
    ...data.filter((m) => m.id !== replaceId),
  ]);
  return tempId;
}

/**
 * Flag an optimistic user turn as transport-failed (kept for inline retry).
 * No refetch — a turn that never persisted would simply be dropped by
 * authoritative data.
 */
export function markUserTurnFailed(
  qc: ReturnType<typeof useQueryClient>,
  conversationId: string,
  tempId: string,
): void {
  const messagesKey = [CONVERSATION_MESSAGES_KEY, conversationId];
  patchNewestMessagesPage(qc, messagesKey, (data) =>
    data.map((m) =>
      m.id === tempId ? { ...m, sendStatus: "failed" as const } : m,
    ),
  );
}


// ---------------------------------------------------------------------------
// Platform Users
// ---------------------------------------------------------------------------

export function useUsers(params?: {
  search?: string;
  gradeCode?: string;
  departmentId?: string;
  active?: boolean;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: queryKeys.users(params as Record<string, unknown> | undefined),
    // Use the jerry lawyers endpoint as the roster source for everyone. It is the
    // business roster (available to any authenticated tenant user) and returns the
    // enriched Lawyer shape — grade, employee number, primary department/position,
    // and roles. The base admin endpoint /auth/users returns bare User rows without
    // that enrichment, so preferring it made admins see 无角色/未设级别 for the whole
    // roster and forced non-admins through a wasted 403 → fallback round-trip.
    // Admin-only mutations (create/update/deactivate) still go through usersApi.
    queryFn: () => lawyersApi.list(params),
  });
}

// ---------------------------------------------------------------------------
// Employee management — grades, departments, positions, roles, staff profiles
//
// These hooks drive the employee roster page (admin only). Data sources span
// jerry (/grades, /staff-profiles), org (/departments, /positions), and rbac
// (/roles, /users/{id}/roles).
// ---------------------------------------------------------------------------

export function useGrades() {
  return useQuery({
    queryKey: ["grades"],
    queryFn: () => gradesApi.list({ pageSize: 100 }),
  });
}

export function useDepartmentsTree() {
  return useQuery({
    queryKey: ["departments", "tree"],
    queryFn: async () => {
      const resp = await departmentsApi.tree();
      return resp.data;
    },
  });
}

export function usePositions() {
  return useQuery({
    queryKey: ["positions"],
    queryFn: async () => {
      const resp = await positionsApi.list();
      return resp.data;
    },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const resp = await rolesApi.list();
      return resp.data;
    },
  });
}

/**
 * Onboard a new employee — serial 4-step creation with compensation rollback.
 *
 * Steps:
 *   1. POST /auth/register { email, password, name, phone } → user_id
 *   2. POST /jerry/staff-profiles { user_id, grade_id, employee_number }
 *   3. (optional) POST /org/departments/{dept}/members { user_id, position_id }
 *   4. (optional) PUT /rbac/users/{user_id}/roles { role_ids: [...] }
 *
 * On any step failure, all previously-created resources are rolled back in
 * reverse order. If rollback itself fails, the error message names the
 * partial state so the admin can fix it from the edit drawer.
 */
export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      email: string;
      password: string;
      name: string;
      phone?: string | null;
      gradeId?: string | null;
      employeeNumber?: string | null;
      departmentId?: string | null;
      positionId?: string | null;
      roleIds?: string[];
    }) => {
      let userId: string | null = null;
      let profileCreated: string | null = null;
      let departmentMemberAdded: string | null = null;

      try {
        // Step 1: create account
        const registerResp = await usersApi.create({
          email: input.email,
          password: input.password,
          name: input.name,
          phone: input.phone ?? null,
        });
        userId = registerResp.data.id;

        // Step 2: attach staff profile (grade + employee number)
        if (input.gradeId || input.employeeNumber) {
          const profileResp = await staffApi.create({
            userId,
            gradeId: input.gradeId ?? null,
            employeeNumber: input.employeeNumber ?? null,
          });
          profileCreated = profileResp.data.id;
        }

        // Step 3: assign to department
        if (input.departmentId) {
          await departmentsApi.addMember(input.departmentId, {
            userId,
            positionId: input.positionId ?? null,
            isPrimary: true,
          });
          departmentMemberAdded = input.departmentId;
        }

        // Step 4: assign roles
        if (input.roleIds && input.roleIds.length > 0) {
          await userRolesApi.assign(userId, input.roleIds);
        }

        return { userId };
      } catch (err) {
        // Rollback in reverse order. Failures during rollback are reported
        // but do not mask the original error.
        const rollbackErrors: string[] = [];
        try {
          if (departmentMemberAdded && userId) {
            await departmentsApi.removeMember(departmentMemberAdded, userId);
          }
        } catch (e) {
          rollbackErrors.push(`部门关系删除失败：${departmentMemberAdded}/${userId}`);
        }
        try {
          if (profileCreated) {
            // DELETE now exists (US-H02); the profile is also cascaded on
            // account delete, but cleaning it up first keeps the rollback
            // self-contained even if the account delete below fails.
            await staffApi.remove(profileCreated);
          }
        } catch (e) {
          rollbackErrors.push(`员工档案清理失败：${userId}`);
        }
        try {
          if (userId) {
            await usersApi.remove(userId);
          }
        } catch (e) {
          rollbackErrors.push(`账号删除失败：${userId}`);
        }

        if (rollbackErrors.length > 0) {
          // Surface both the original failure and the rollback residue so the
          // admin knows exactly what to clean up manually.
          const baseMsg = err instanceof Error ? err.message : String(err);
          throw new Error(
            `${baseMsg} | 入职未完成，需手工清理：${rollbackErrors.join("；")}`,
          );
        }
        throw err;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["lawyers"] });
      qc.invalidateQueries({ queryKey: ["departments"] });
    },
  });
}

/** Update an existing user's account fields (name/email/is_active). */
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: UserUpdatePayload }) =>
      usersApi.update(userId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["lawyers"] });
    },
  });
}

/** Update staff profile (grade/employee_number). */
export function useUpdateStaffProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      profileId,
      payload,
    }: {
      profileId: string;
      payload: StaffProfileUpdatePayload;
    }) => staffApi.update(profileId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lawyers"] });
    },
  });
}

/** Assign roles to a user (PUT replaces the full set). */
export function useAssignUserRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleIds }: { userId: string; roleIds: string[] }) =>
      userRolesApi.assign(userId, roleIds),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ["lawyers"] });
      qc.invalidateQueries({ queryKey: ["audit-logs", "User", userId] });
    },
  });
}

/** Soft-delete a staff profile (US-H02). */
export function useDeleteStaffProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) => staffApi.remove(profileId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lawyers"] });
    },
  });
}

/** Backfill a staff profile for a pre-existing user (US-H02). */
export function useCreateStaffProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StaffProfileCreatePayload) => staffApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lawyers"] });
    },
  });
}

/** Soft-delete a user account (US-H06, danger zone). */
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => usersApi.remove(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lawyers"] });
    },
  });
}

/** Employee audit trail — merges the User and StaffProfile dimensions (US-H04). */
export function useEmployeeAuditLogs(userId: string | null, profileId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["audit-logs", "employee", userId, profileId],
    enabled: enabled && !!userId,
    queryFn: async () => {
      const entries: Awaited<ReturnType<typeof auditLogsApi.forResource>>["data"] = [];
      const requests: Promise<void>[] = [];
      if (userId) {
        requests.push(
          auditLogsApi.forResource("User", userId, 50).then((r) => { entries.push(...r.data); }),
        );
      }
      if (profileId) {
        requests.push(
          auditLogsApi.forResource("StaffProfile", profileId, 50).then((r) => { entries.push(...r.data); }),
        );
      }
      await Promise.all(requests);
      entries.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
      return entries;
    },
  });
}

/** Add a user to a department (or move primary department by remove+add). */
export function useAddDepartmentMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      departmentId,
      payload,
    }: {
      departmentId: string;
      payload: DepartmentMemberAddPayload;
    }) => departmentsApi.addMember(departmentId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lawyers"] });
      qc.invalidateQueries({ queryKey: ["departments"] });
    },
  });
}

/** Remove a user from a department. */
export function useRemoveDepartmentMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ departmentId, userId }: { departmentId: string; userId: string }) =>
      departmentsApi.removeMember(departmentId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lawyers"] });
      qc.invalidateQueries({ queryKey: ["departments"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Tasks — mutations
// ---------------------------------------------------------------------------

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      matterId?: string | null;
      title: string;
      type?: string;
      priority?: string;
      source?: string;
      assigneeId?: string | null;
      deadlineAt?: string | null;
      notes?: string;
    }) =>
      tasksApi.create({
        matterId: data.matterId,
        title: data.title,
        type: data.type ?? "review",
        source: data.source ?? "superior_assigned",
        priority: (data.priority ?? "normal") as "urgent" | "high" | "normal" | "low",
        assigneeId: data.assigneeId ?? undefined,
        deadlineAt: data.deadlineAt,
        notes: data.notes,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["my-tasks"] });
      qc.invalidateQueries({ queryKey: ["compass"] });
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string; title?: string; type?: string; priority?: "urgent" | "high" | "normal" | "low"; source?: string; assigneeId?: string | null; deadlineAt?: string | null; notes?: string | null }) =>
      tasksApi.update(data.id, {
        title: data.title,
        type: data.type,
        priority: data.priority,
        source: data.source,
        assigneeId: data.assigneeId ?? undefined,
        deadlineAt: data.deadlineAt,
        notes: data.notes,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["my-tasks"] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["my-tasks"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Documents — mutations
// ---------------------------------------------------------------------------

/**
 * 上传文档。`matterId` 为空时上传到调用方的个人空间（`ownerScope=personal`，
 * 后端 `_check_owner_scope_eligibility` 对 personal 不要求事项）——律师不必先建
 * 客户与事项就能把一份合同放进系统并发起审查；个人文档事后可经「转移归属」
 * 挂到事项。有事项时走原有的事项归属上传，行为不变。
 */
export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { matterId: string | null; title: string; type: string; file: File }) =>
      data.matterId
        ? documentsApi.upload({ ...data, matterId: data.matterId })
        : documentsApi.uploadWithScope({
            file: data.file,
            title: data.title,
            type: data.type,
            ownerScope: "personal",
          }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["personal-documents"] });
      // Document list queries live under three distinct roots:
      // - matter-scoped flat list `["documents", ...]`
      // - cross-matter `["workbench-documents", ...]`
      // - matter tree `["matter-tree", ...]` (used by MatterDocuments tab)
      // All three must be invalidated or the freshly uploaded file won't
      // show up — MatterDocuments uses the tree, the workbench page uses
      // workbench-documents, and CompareDocumentPicker uses documents.
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["workbench-documents"] });
      qc.invalidateQueries({ queryKey: ["matter-tree"] });
      qc.removeQueries({ queryKey: ["document-preview"] });
      qc.invalidateQueries({ queryKey: ["document-preview"] });
    },
  });
}

/**
 * Upload a document as a conversation attachment (in-chat document upload).
 * The file enters the document library, so the shared document lists are
 * invalidated on success. The mutation resolves to the created Document (with
 * its initial `ragIndexStatus`), which the composer needs to seed the chip.
 */
export function useUploadConversationAttachment(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, title }: { file: File; title?: string }) =>
      conversationsApi
        .uploadAttachment(conversationId, file, title)
        .then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["workbench-documents"] });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => documentsApi.delete(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["matter-tree"] });
      // New version uploads / deletes must force preview refetch.
      qc.removeQueries({ queryKey: ["document-preview", id] });
      qc.invalidateQueries({ queryKey: ["document-preview", id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Deadlines
// ---------------------------------------------------------------------------

export function useDeadlines(params?: { matterId?: string; status?: string; page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: queryKeys.deadlines(params as Record<string, unknown> | undefined),
    queryFn: () => deadlinesApi.list(params),
  });
}

export function useDeadline(deadlineId: string | null | undefined) {
  return useQuery({
    queryKey: ["jerry", "deadline", deadlineId],
    enabled: !!deadlineId,
    queryFn: () => deadlinesApi.get(deadlineId as string),
  });
}

export function useCreateDeadline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deadlinesApi.create>[0]) =>
      deadlinesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deadlines"] });
    },
  });
}

export function useUpdateDeadline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof deadlinesApi.update>[1] }) =>
      deadlinesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deadlines"] });
    },
  });
}

export function useDeleteDeadline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deadlinesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deadlines"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Audit Logs
// ---------------------------------------------------------------------------

export function useAuditLogs(params?: {
  resourceType?: string;
  resourceId?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["audit-logs", params] as const,
    queryFn: () =>
      auditLogsApi
        .forResource(params!.resourceType!, params!.resourceId!, params?.limit)
        .then((res) => res.data),
    enabled: !!params?.resourceType && !!params?.resourceId,
  });
}

/**
 * AF3 — 事项活动时间线。聚合该事项自身（Matter / matter_brief）+ 9 类子实体
 * 的审计事件，由后端 `GET /matters/:id/timeline` 单条 UNION ALL 完成。保留
 * `useAuditLogs` 不动（ClientTimeline 等组件仍依赖单资源查询）。
 */
export function useMatterTimeline(
  matterId: string,
  params?: { page?: number; pageSize?: number },
) {
  return useQuery({
    queryKey: ["matter-timeline", matterId, params] as const,
    queryFn: () =>
      mattersApi.matterTimeline.list(matterId, params).then((res) => res.data),
    enabled: !!matterId,
  });
}

// ---------------------------------------------------------------------------
// Auth — current user
// ---------------------------------------------------------------------------

export function useCurrentUser() {
  return useQuery({
    queryKey: ["current-user"] as const,
    queryFn: () => authApi.me().then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.updateMe,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["current-user"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Time Entries
// ---------------------------------------------------------------------------

export function useTimeEntries(
  params?: {
    matterId?: string;
    employeeId?: string;
    actionTypeId?: string;
    status?: "running" | "stopped";
    billingStatus?: "billable" | "non_billable" | "billed";
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.timeEntries(params as Record<string, unknown> | undefined),
    queryFn: () => timeEntriesApi.list(params),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TimeEntryCreatePayload) => timeEntriesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["time-entries"] });
      qc.invalidateQueries({ queryKey: ["time-entries-summary"] });
    },
  });
}

export function useStopTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => timeEntriesApi.stop(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["time-entries"] });
      qc.invalidateQueries({ queryKey: ["time-entries-summary"] });
    },
  });
}

export function useUpdateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TimeEntryUpdatePayload }) =>
      timeEntriesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["time-entries"] });
      qc.invalidateQueries({ queryKey: ["time-entries-summary"] });
    },
  });
}

export function useDeleteTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => timeEntriesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["time-entries"] });
      qc.invalidateQueries({ queryKey: ["time-entries-summary"] });
    },
  });
}

/** Time-entry summary. Pass an object with any combination of matterId /
 * employeeId / dateFrom / dateTo. Omit all four for a tenant-wide total. */
export function useTimeEntrySummary(params?: {
  matterId?: string;
  employeeId?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  return useQuery({
    queryKey: queryKeys.timeEntrySummary(params as Record<string, unknown> | undefined),
    queryFn: () => timeEntriesApi.summary(params),
  });
}

// ---------------------------------------------------------------------------
// Action Types & Staff Profiles
// ---------------------------------------------------------------------------

export function useActionTypes(params?: { isActive?: boolean; page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: queryKeys.actionTypes(params as Record<string, unknown> | undefined),
    queryFn: () => actionTypesApi.list(params),
  });
}

export function useStaffProfiles(params?: { page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: queryKeys.staffProfiles(params as Record<string, unknown> | undefined),
    queryFn: () => staffProfilesApi.list(params),
  });
}

export function useMyStaffProfile() {
  return useQuery({
    queryKey: queryKeys.myStaffProfile,
    queryFn: () => staffProfilesApi.me(),
  });
}

// ---------------------------------------------------------------------------
// Resources (Knowledge Assets)
// ---------------------------------------------------------------------------

export function useResources(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["resources", params] as const,
    queryFn: () => resourcesApi.list(params),
  });
}

export function useCreateResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof resourcesApi.create>[0]) => resourcesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resources"] });
    },
  });
}

export function useUpdateResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof resourcesApi.update>[1] }) =>
      resourcesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resources"] });
    },
  });
}

export function useDeleteResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resourcesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resources"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Document metadata & folders
// ---------------------------------------------------------------------------

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; title?: string; type?: string }) =>
      documentsApi.update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["matter-tree"] });
      // Metadata/version changes must force preview refetch.
      qc.removeQueries({ queryKey: ["document-preview", id] });
      qc.invalidateQueries({ queryKey: ["document-preview", id] });
    },
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { matterId: string; title: string; parentId?: string }) =>
      documentsApi.createFolder(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matter-tree"] });
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useMatterTreeChildren(matterId: string, parentId?: string) {
  return useQuery({
    queryKey: ["matter-tree", matterId, parentId] as const,
    queryFn: () => documentsApi.matterTree(matterId, parentId).then((res) => res.data),
    enabled: !!matterId,
  });
}

// ---------------------------------------------------------------------------
// Capabilities (AI Capabilities)
// ---------------------------------------------------------------------------

const CAPABILITIES_KEY = ["capabilities"] as const;

export function useCapabilities(
  params?: Parameters<typeof capabilitiesApi.list>[0],
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...CAPABILITIES_KEY, params],
    queryFn: () => capabilitiesApi.list(params),
    enabled: options?.enabled,
  });
}

export function useCapability(id: string) {
  return useQuery({
    queryKey: [...CAPABILITIES_KEY, id],
    queryFn: () => capabilitiesApi.get(id).then((res) => res.data),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Custom Advisors (US-G03 律所自定义顾问)
// ---------------------------------------------------------------------------

const CUSTOM_ADVISORS_KEY = ["custom-advisors"] as const;

/**
 * Full detail (incl. systemPrompt/tools) for one custom advisor —
 * manage-access only. A 403 (surfaced as `isError`) is the signal the caller
 * uses to hide edit/停用/删除 entry points, rather than replicating the
 * backend's creator/`*_asset:admin` manage-access matrix client-side.
 * `enabled` lets callers gate the fetch to only custom-advisor cards
 * (identified via `isCustomAdvisorCard`) — never probed for seed/manual cards.
 */
export function useCustomAdvisor(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...CUSTOM_ADVISORS_KEY, id],
    queryFn: () => customAdvisorsApi.get(id).then((res) => res.data),
    enabled: (options?.enabled ?? true) && !!id,
    retry: false,
  });
}

/**
 * Register a new custom advisor. Engine registration + local write are
 * atomic server-side (BR-G03-3) — success means the card is live and visible
 * both in the capability catalog and the advisor directory, so both caches
 * are invalidated together.
 */
export function useCreateCustomAdvisor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CustomAdvisorCreateRequest) =>
      customAdvisorsApi.create(data).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
      qc.invalidateQueries({ queryKey: ADVISORS_KEY });
    },
  });
}

/** Edit and/or 启用停用 (S3-04). A stopped advisor must drop out of the
 * dispatch candidate pool and the picker immediately (US-G01 BR-G01-6). */
export function useUpdateCustomAdvisor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CustomAdvisorUpdateRequest }) =>
      customAdvisorsApi.update(id, data).then((res) => res.data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CUSTOM_ADVISORS_KEY, id] });
      qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
      qc.invalidateQueries({ queryKey: ADVISORS_KEY });
    },
  });
}

/** Uninstalls the engine addon + soft-deletes the card (BR-G03-3 双向同步). */
export function useDeleteCustomAdvisor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customAdvisorsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
      qc.invalidateQueries({ queryKey: ADVISORS_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Prompt Templates (quick-prompt picker for the chat composer, W7)
// ---------------------------------------------------------------------------

const PROMPT_TEMPLATES_KEY = ["prompt-templates"] as const;

/** Quick-prompt templates for the composer picker. Enable lazily (e.g. only when
 *  the picker opens) via the `enabled` flag to avoid a request on every mount. */
export function usePromptTemplates(enabled = true) {
  return useQuery({
    queryKey: PROMPT_TEMPLATES_KEY,
    queryFn: () => promptTemplatesApi.list().then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/** Rewrite a rough composer input into a full prompt (W8). Mutation — resolves to
 *  the optimized text; on failure the caller keeps the lawyer's original input. */
export function useOptimizePrompt() {
  return useMutation({
    mutationFn: (vars: { content: string; context?: string }) =>
      promptOptimizationApi.optimize(vars.content, vars.context),
  });
}

export function useCreateCapability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CapabilityCreateRequest) =>
      capabilitiesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
    },
  });
}

export function useUpdateCapability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CapabilityUpdateRequest }) =>
      capabilitiesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
    },
  });
}

export function useDeleteCapability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => capabilitiesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
    },
  });
}

export function useToggleCapabilityActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => capabilitiesApi.toggleActive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
    },
  });
}

export function useToggleCapabilityStar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => capabilitiesApi.toggleStar(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: CAPABILITIES_KEY });
      const previousData = qc.getQueriesData({ queryKey: CAPABILITIES_KEY });
      qc.setQueriesData<PaginatedResponse<Capability>>(
        { queryKey: CAPABILITIES_KEY },
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((cap) =>
              cap.id === id ? { ...cap, isStarred: !cap.isStarred } : cap
            ),
          };
        }
      );
      return { previousData };
    },
    onError: (_err, _id, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          qc.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// MCP Servers
// ---------------------------------------------------------------------------

const MCP_SERVERS_KEY = ["mcp-servers"] as const;
const MCP_CONNECTORS_KEY = ["mcp-connectors"] as const;

/** List MCP servers bound to the current tenant. */
export function useMcpServers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: MCP_SERVERS_KEY,
    queryFn: () => mcpServersApi.list(),
    enabled: options?.enabled ?? true,
  });
}

/**
 * 外部数据源统一视图的查询键。对话发送被 422 拒绝（授权期间过期、绑定被撤）时
 * 由聊天钩子失效它，让选择面上的接入状态立刻跟上。
 */
export const CONNECTORS_KEY = ["connectors"] as const;

/**
 * 全部外部数据源（含本租户接入状态与本人授权状态）。
 *
 * 目录量级是个位数且很少变动，因此设 5 分钟 staleTime——输入框每次挂载都打一次
 * 请求没有意义；接入状态真的变了的时刻（发送被拒、授权回调）由显式失效驱动。
 */
export function useConnectors(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: CONNECTORS_KEY,
    queryFn: () => connectorsApi.list(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

/** 内置企业连接器目录（含本租户接入状态）。 */
export function useMcpConnectors(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: MCP_CONNECTORS_KEY,
    queryFn: () => mcpServersApi.connectors(),
    enabled: options?.enabled ?? true,
  });
}

/** Register a new MCP server binding. */
export function useCreateMcpServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: McpServerRegisterRequest) => mcpServersApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MCP_SERVERS_KEY });
      // 新绑定可能命中内置连接器（同名关联）——刷新连接器接入状态。
      qc.invalidateQueries({ queryKey: MCP_CONNECTORS_KEY });
    },
  });
}

/** Trigger a sync of a single MCP server's tool catalog. */
export function useSyncMcpServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mcpServersApi.sync(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MCP_SERVERS_KEY });
    },
  });
}

/** Fetch the tool catalog exposed by a single MCP server. */
export function useMcpServerTools(id: string | null | undefined) {
  return useQuery({
    queryKey: [...MCP_SERVERS_KEY, id, "tools"] as const,
    queryFn: () => mcpServersApi.tools(id as string),
    enabled: !!id,
  });
}

/** Update an MCP server binding (is_enabled / auto_sync). */
export function useUpdateMcpServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: McpBindingPatch }) =>
      mcpServersApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MCP_SERVERS_KEY });
    },
  });
}

/** Delete / unbind an MCP server. Pass cascade=true to also remove the
 *  derived mcp_tool capabilities. */
export function useDeleteMcpServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cascade }: { id: string; cascade?: boolean }) =>
      mcpServersApi.delete(id, cascade),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MCP_SERVERS_KEY });
      // 解绑可能使内置连接器回到「需配置」——刷新连接器接入状态。
      qc.invalidateQueries({ queryKey: MCP_CONNECTORS_KEY });
      // Derived mcp_tool capabilities may have changed — refresh the
      // capabilities cache too.
      qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Engine catalog (任务二阶段二：能力卡「绑定引擎目标」选择器)
// ---------------------------------------------------------------------------

const ENGINE_CATALOG_KEY = ["engine-catalog"] as const;

/**
 * 可绑定引擎目标目录（builtin agent 名 + 激活 skill id），创建/编辑能力卡
 * 弹窗的选择器数据源。引擎不可达时后端 502 —— `isError` 即选择器的
 * 「目录暂不可用」态，不静默给空目录。`enabled` 供弹窗按类型（仅
 * skill/agent 可绑定）与开合状态控制拉取。
 */
export function useEngineCatalog(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ENGINE_CATALOG_KEY,
    queryFn: () => capabilitiesApi.getEngineCatalog().then((res) => res.data),
    enabled: options?.enabled ?? true,
    retry: false,
    // 60 秒 staleTime：与后端目录接口的 60s TTL 对齐（评审 V14）——创建/
    // 编辑弹窗共用一个 key，短 TTL 目录不值得每次打开弹窗都打一次引擎。
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Knowledge (Business Knowledge)
// ---------------------------------------------------------------------------

const KNOWLEDGE_KEY = ["knowledge"] as const;

export function useKnowledgeItems(params?: Parameters<typeof knowledgeApi.list>[0]) {
  return useQuery({
    queryKey: [...KNOWLEDGE_KEY, params],
    queryFn: () => knowledgeApi.list(params),
  });
}

// ---------------------------------------------------------------------------
// 合同要素批量提取任务（1B-3）
// ---------------------------------------------------------------------------

const EXTRACTION_JOB_POLL_MS = 3000;

function isExtractionJobTerminal(status: string | undefined): boolean {
  return status === "succeeded" || status === "failed";
}

export function useExtractionJobs(params?: { matterId?: string; page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: ["extraction-jobs", params ?? {}],
    queryFn: () => extractionJobsApi.list(params),
    // 列表里有未终态任务时轮询，让状态列自己前进。
    refetchInterval: (query) =>
      (query.state.data?.data ?? []).some((j) => !isExtractionJobTerminal(j.status))
        ? EXTRACTION_JOB_POLL_MS
        : false,
  });
}

export function useExtractionJob(jobId: string | undefined) {
  return useQuery({
    queryKey: ["extraction-job", jobId ?? ""],
    queryFn: () => extractionJobsApi.get(jobId as string).then((res) => res.data),
    enabled: !!jobId,
    refetchInterval: (query) =>
      query.state.data && !isExtractionJobTerminal(query.state.data.status)
        ? EXTRACTION_JOB_POLL_MS
        : false,
  });
}

export function useCreateExtractionJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ExtractionJobCreateRequest) =>
      extractionJobsApi.create(data).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["extraction-jobs"] });
    },
  });
}

/** 导出要素表 Excel。409 = 任务尚未完成。 */
export function useExtractionJobExportDownload(): TriggeredDownload {
  return useBlobDownload(downloadExtractionJobExport, {
    403: "无权限导出该提取任务",
    404: "提取任务不存在或已被删除",
    409: "提取尚未完成，暂无可导出的要素表",
  });
}

/**
 * 源文档表格告警文案（2026-08-26 表格插入批次，评估文档轴四）——三个
 * "打开为草稿"入口（以此起草 / 打开为草稿 / 以此范本起草）共用同一条
 * 文案与展示时长，均由 `sourceHasTables` 驱动。集中在这三个 mutation hook
 * 的 `onSuccess` 里触发（而非分散到各页面的调用处）：三个入口物理上就是
 * 这三个 hook 的全部消费方，在这一层触发能一次覆盖，不漏掉任何一个
 * 调用点，也不需要每新增一个调用点就记得补一次告警。
 */
const SOURCE_HAS_TABLES_WARNING = "源文档含表格，表格内容未能带入正文";

/** 1A-4：从范本卡「以此范本起草」——范本正文落入新草稿，调用方跳 /drafts/{id}。 */
export function useOpenKnowledgeAsDraftDoc() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { knowledgeId: string; title?: string; matterId?: string }) =>
      knowledgeApi.openAsDraftDoc(vars.knowledgeId, {
        ...(vars.title ? { title: vars.title } : {}),
        ...(vars.matterId ? { matterId: vars.matterId } : {}),
      }),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["draft-docs"] });
      if (resp.sourceHasTables) {
        message.warning(SOURCE_HAS_TABLES_WARNING);
      }
    },
    onError: (err) => {
      message.error(isApiError(err) ? err.message : "以范本起草失败，请重试");
    },
  });
}

export function useKnowledgeItem(id: string) {
  return useQuery({
    queryKey: [...KNOWLEDGE_KEY, id],
    queryFn: () => knowledgeApi.get(id).then((res) => res.data),
    enabled: !!id,
  });
}

export function useCreateKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: KnowledgeItemCreateRequest) =>
      knowledgeApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY });
    },
  });
}

export function useUploadKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => knowledgeApi.upload(formData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY });
    },
  });
}

export function useUpdateKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: KnowledgeItemUpdateRequest }) =>
      knowledgeApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY });
    },
  });
}

export function useDeleteKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => knowledgeApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY });
    },
  });
}

export function useToggleKnowledgeActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => knowledgeApi.toggleActive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Knowledge Categories
// ---------------------------------------------------------------------------

const KNOWLEDGE_CATEGORIES_KEY = ["knowledgeCategories"] as const;

export function useKnowledgeCategories() {
  return useQuery({
    queryKey: KNOWLEDGE_CATEGORIES_KEY,
    queryFn: () => knowledgeCategoriesApi.list().then((res) => res.data),
  });
}

const EXTERNAL_LINKS_KEY = ["externalLinks"] as const;

export function useExternalLinks() {
  return useQuery({
    queryKey: EXTERNAL_LINKS_KEY,
    queryFn: () => externalLinksApi.list().then((res) => res.data),
  });
}

export function useCreateKnowledgeCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: KnowledgeCategoryCreateRequest) =>
      knowledgeCategoriesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_CATEGORIES_KEY });
    },
  });
}

export function useUpdateKnowledgeCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: KnowledgeCategoryUpdateRequest }) =>
      knowledgeCategoriesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_CATEGORIES_KEY });
    },
  });
}

export function useMoveKnowledgeCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: KnowledgeCategoryMoveRequest }) =>
      knowledgeCategoriesApi.move(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_CATEGORIES_KEY });
    },
  });
}

export function useDeleteKnowledgeCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => knowledgeCategoriesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_CATEGORIES_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Pipeline Executions
// ---------------------------------------------------------------------------

const PIPELINE_EXECUTIONS_KEY = ["pipeline-executions"] as const;

export function usePipelineExecutions(params?: {
  status?: string;
  triggerSource?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: [...PIPELINE_EXECUTIONS_KEY, params],
    queryFn: () => pipelineExecutionsApi.list(params),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.data) return false;
      const hasRunning = data.data.some((w) => w.status === "running" || w.status === "pending");
      return hasRunning ? 5000 : false;
    },
  });
}

export function usePipelineExecution(id: string) {
  return useQuery({
    queryKey: [...PIPELINE_EXECUTIONS_KEY, id],
    queryFn: () => pipelineExecutionsApi.get(id).then((res) => res.data),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return data.status === "running" || data.status === "pending" ? 5000 : false;
    },
  });
}

export function useCancelPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pipelineExecutionsApi.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_EXECUTIONS_KEY });
    },
  });
}

export function useRetryPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pipelineExecutionsApi.retry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_EXECUTIONS_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Pipeline Templates
// ---------------------------------------------------------------------------

const PIPELINE_TEMPLATES_KEY = ["pipeline-templates"] as const;

export function usePipelineTemplates(params?: {
  scope?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: [...PIPELINE_TEMPLATES_KEY, params],
    queryFn: () => pipelineTemplatesApi.list(params),
  });
}

export function usePipelineTemplate(id: string) {
  return useQuery({
    queryKey: [...PIPELINE_TEMPLATES_KEY, id],
    queryFn: () => pipelineTemplatesApi.get(id).then((res) => res.data),
    enabled: !!id,
  });
}

export function useCreatePipelineTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof pipelineTemplatesApi.create>[0]) =>
      pipelineTemplatesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_TEMPLATES_KEY });
    },
  });
}

export function useUpdatePipelineTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof pipelineTemplatesApi.update>[1] }) =>
      pipelineTemplatesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_TEMPLATES_KEY });
    },
  });
}

export function useDeletePipelineTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pipelineTemplatesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_TEMPLATES_KEY });
    },
  });
}

/** Template detail with parsed content summary (detail drawer + wizard inputs). */
export function usePipelineTemplateDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: [...PIPELINE_TEMPLATES_KEY, "detail", id],
    queryFn: () => pipelineTemplatesApi.getDetail(id as string).then((res) => res.data),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Pipeline Instances (three-tier middle layer — iteration 6 stage 2)
// ---------------------------------------------------------------------------

const PIPELINE_INSTANCES_KEY = ["pipeline-instances"] as const;

/**
 * Full instance detail (instance + computed names + recent executions).
 * Refetches every 5 s while queued / running so the Runs tab picks up new
 * executions without a manual reload.
 */
export function usePipelineInstance(instanceId: string) {
  return useQuery({
    queryKey: [...PIPELINE_INSTANCES_KEY, instanceId],
    queryFn: () => pipelineInstancesApi.get(instanceId).then((res) => res.data),
    enabled: !!instanceId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return data.status === "queued" || data.status === "running" ? 5000 : false;
    },
  });
}

/**
 * Paginated instance list. Refetches every 5 s while any visible instance is
 * queued / running so progress surfaces without a manual reload.
 */
export function usePipelineInstances(
  filters: {
    status?: string;
    matterId?: string;
    scope: "mine" | "all";
    search?: string;
  },
  pagination: { page: number; pageSize: number },
) {
  return useQuery({
    queryKey: [...PIPELINE_INSTANCES_KEY, "list", filters, pagination],
    queryFn: () =>
      pipelineInstancesApi.list({
        status: filters.status,
        matterId: filters.matterId,
        scope: filters.scope,
        search: filters.search,
        page: pagination.page,
        pageSize: pagination.pageSize,
      }),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.data) return false;
      const hasActive = data.data.some(
        (i) => i.status === "queued" || i.status === "running",
      );
      return hasActive ? 5000 : false;
    },
  });
}

/**
 * Mutations covering the draft → queued → running lifecycle. Each invalidates
 * both the list (`PIPELINE_INSTANCES_KEY`) and the open instance detail so the
 * table and the wizard/detail view stay in sync after every step.
 */
export function useCreatePipelineInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PipelineInstanceCreatePayload) =>
      pipelineInstancesApi.create(payload).then((res) => res.data),
    onSuccess: (instance) => {
      qc.invalidateQueries({ queryKey: PIPELINE_INSTANCES_KEY });
      qc.invalidateQueries({ queryKey: [...PIPELINE_INSTANCES_KEY, instance.id] });
    },
  });
}

export function useUpdatePipelineInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; data: PipelineInstanceUpdatePayload }) =>
      pipelineInstancesApi.update(vars.id, vars.data).then((res) => res.data),
    onSuccess: (instance, vars) => {
      qc.invalidateQueries({ queryKey: PIPELINE_INSTANCES_KEY });
      qc.invalidateQueries({ queryKey: [...PIPELINE_INSTANCES_KEY, vars.id] });
      qc.setQueryData([...PIPELINE_INSTANCES_KEY, vars.id], instance);
    },
  });
}

export function useReadyPipelineInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; executorConfig?: ExecutorConfigPayload }) =>
      pipelineInstancesApi
        .ready(vars.id, { executorConfig: vars.executorConfig })
        .then((res) => res.data),
    onSuccess: (instance, vars) => {
      qc.invalidateQueries({ queryKey: PIPELINE_INSTANCES_KEY });
      qc.invalidateQueries({ queryKey: [...PIPELINE_INSTANCES_KEY, vars.id] });
      qc.setQueryData([...PIPELINE_INSTANCES_KEY, vars.id], instance);
    },
  });
}

export function useRunPipelineInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      pipelineInstancesApi.run(id).then((res) => ({
        executionId: res.data.execution_id,
        instance: res.data.instance,
      })),
    onSuccess: (_result, id) => {
      qc.invalidateQueries({ queryKey: PIPELINE_INSTANCES_KEY });
      qc.invalidateQueries({ queryKey: [...PIPELINE_INSTANCES_KEY, id] });
      // Bust the executions cache so the Runs tab picks up the new row.
      qc.invalidateQueries({ queryKey: PIPELINE_EXECUTIONS_KEY });
    },
  });
}

export function useDeletePipelineInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pipelineInstancesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_INSTANCES_KEY });
    },
  });
}

export function usePausePipelineInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pipelineInstancesApi.pause(id).then((res) => res.data),
    onSuccess: (instance, id) => {
      qc.invalidateQueries({ queryKey: PIPELINE_INSTANCES_KEY });
      qc.invalidateQueries({ queryKey: [...PIPELINE_INSTANCES_KEY, id] });
      qc.setQueryData([...PIPELINE_INSTANCES_KEY, id], instance);
    },
  });
}

export function useResumePipelineInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; strategy?: "retry" | "skip" }) =>
      pipelineInstancesApi.resume(vars.id, vars.strategy).then((res) => res.data),
    onSuccess: (instance, vars) => {
      qc.invalidateQueries({ queryKey: PIPELINE_INSTANCES_KEY });
      qc.invalidateQueries({ queryKey: [...PIPELINE_INSTANCES_KEY, vars.id] });
      qc.setQueryData([...PIPELINE_INSTANCES_KEY, vars.id], instance);
    },
  });
}

// ---------------------------------------------------------------------------
// VeriDoc — document comparison
// ---------------------------------------------------------------------------

/** Upload a pair of PDF/DOCX files into one temporary veridoc document. */
export function useVeridocUpload() {
  return useMutation({
    mutationFn: (data: Parameters<typeof veridocApi.upload>[0]) =>
      veridocApi.upload(data),
  });
}

/** Create a comparison between two versions; resolves with the full changes. */
export function useVeridocCreateComparison() {
  return useMutation({
    mutationFn: (data: Parameters<typeof veridocApi.createComparison>[0]) =>
      veridocApi.createComparison(data),
  });
}

/**
 * Create a comparison between two existing jerry documents via the jerry wrapper
 * (POST /api/v1/jerry/documents/{source}/compare-against/{target}).
 *
 * Returns jerry's stable comparison id + veridoc's comparison id + status.
 */
export function useCreateComparison() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof documentsApi.compareDocuments>[0]) =>
      documentsApi.compareDocuments(data),
    onSuccess: () => {
      // Bare-prefix invalidation: a new comparison can shift every document's
      // comparison list, so we drop ALL `vd-comparison*` entries and let each
      // consumer refetch on demand. VD_COMPARISON_KEY is id-scoped, so we
      // intentionally use the raw prefix here.
      queryClient.invalidateQueries({ queryKey: ["vd-comparison"] });
    },
  });
}

const VD_COMPARISON_KEY = (jerryCmpId: string) => ["vd-comparison", jerryCmpId] as const;
const VD_ANNOTATIONS_KEY = (jerryCmpId: string, changeId?: string) =>
  changeId
    ? (["vd-annotations", jerryCmpId, changeId] as const)
    : (["vd-annotations", jerryCmpId] as const);

/**
 * Convert jerry wrapper's verb-form decision (`accept`/`reject`/`pending`) into
 * veridoc's adjective-form `ChangeStatus` (`accepted`/`rejected`/`pending`)
 * used inside `ComparisonChange.currentStatus`.
 */
function decisionToChangeStatus(s: DecisionStatus): ChangeStatus {
  if (s === DECISION_STATUS.ACCEPT) return CHANGE_STATUS.ACCEPTED;
  if (s === DECISION_STATUS.REJECT) return CHANGE_STATUS.REJECTED;
  return CHANGE_STATUS.PENDING;
}

/**
 * Fetch full comparison detail (changes list) and cache it for optimistic updates.
 *
 * Polls every 1.5s while the comparison is still `processing` (veridoc parsing /
 * diffing a large document can outlast the first fetch), and stops once it
 * reaches a terminal `ready` / `error` status. Without this, a slow comparison
 * leaves the UI stuck on "正在处理中" forever (P2-1).
 */
export function useComparison(jerryCmpId: string | null | undefined) {
  return useQuery({
    queryKey: VD_COMPARISON_KEY(jerryCmpId ?? ""),
    queryFn: () => veridocApi.getComparisonDetail(jerryCmpId as string),
    enabled: !!jerryCmpId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return data.status === COMPARISON_STATUS.PROCESSING ? 1500 : false;
    },
  });
}

/**
 * b4: reverse-lookup a jerry comparison by veridoc comparison id.
 *
 * Powers the share-link redirect entry at `/compare/:vdCmpId` — on resolve,
 * the page redirects to `/workbench/<targetId>?mode=compare&cmp=<vdCmpId>`.
 * 404/403 from the backend surface as an error state (no retry, no polling —
 * the lookup is a single shot; bad links shouldn't hammer the server).
 */
export function useComparisonByVeridocId(vdCmpId: string | null | undefined) {
  return useQuery({
    queryKey: ["jerry-comparison-by-veridoc", vdCmpId ?? ""],
    queryFn: () => veridocApi.getComparisonByVeridocId(vdCmpId as string),
    enabled: !!vdCmpId,
    retry: false,
  });
}

/**
 * Set accept/reject/pending on a single change with optimistic update.
 *
 * Matches the change by `c.id === changeId` (not `changeId` field — that's the
 * wrapper's response field, not the change row's own id), and converts the
 * verb-form `DecisionStatus` into the adjective-form `ChangeStatus` before
 * writing into `currentStatus`. Rolls back to the prior cache on error.
 */
export function useSetChangeDecision(jerryCmpId: string) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { changeId: string; status: DecisionStatus }) =>
      veridocApi.setDecision(jerryCmpId, vars.changeId, vars.status),
    onMutate: async (vars) => {
      const queryKey = VD_COMPARISON_KEY(jerryCmpId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ComparisonResponse>(queryKey);
      queryClient.setQueryData<ComparisonResponse>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          changes: old.changes.map((c) =>
            c.id === vars.changeId
              ? { ...c, currentStatus: decisionToChangeStatus(vars.status) }
              : c,
          ),
        };
      });
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          VD_COMPARISON_KEY(jerryCmpId),
          context.previous,
        );
      }
      const text = isApiError(err) ? err.message : "操作失败，请重试";
      message.error(text);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: VD_COMPARISON_KEY(jerryCmpId) });
    },
  });
}

/** Fetch annotations for a single change, keyed per-change to avoid cross-invalidation. */
export function useAnnotations(jerryCmpId: string, changeId: string) {
  return useQuery({
    queryKey: VD_ANNOTATIONS_KEY(jerryCmpId, changeId),
    queryFn: () => veridocApi.listAnnotations(jerryCmpId, changeId),
    // Both ids are required to form a valid path; the panel mounts with empty
    // ids before a change is opened, so stay idle until both are present.
    enabled: !!jerryCmpId && !!changeId,
  });
}

/** Create an annotation; invalidates both the annotation list and comparison detail. */
export function useCreateAnnotation(jerryCmpId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { changeId: string; body: string }) =>
      veridocApi.createAnnotation(jerryCmpId, vars.changeId, vars.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VD_ANNOTATIONS_KEY(jerryCmpId) });
      queryClient.invalidateQueries({ queryKey: VD_COMPARISON_KEY(jerryCmpId) });
    },
  });
}

/** Update an annotation; invalidates both lists. */
export function useUpdateAnnotation(jerryCmpId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { annotationId: string; body: string }) =>
      veridocApi.updateAnnotation(jerryCmpId, vars.annotationId, vars.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VD_ANNOTATIONS_KEY(jerryCmpId) });
      queryClient.invalidateQueries({ queryKey: VD_COMPARISON_KEY(jerryCmpId) });
    },
  });
}

/** Delete an annotation; invalidates both lists. */
export function useDeleteAnnotation(jerryCmpId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { annotationId: string }) =>
      veridocApi.deleteAnnotation(jerryCmpId, vars.annotationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VD_ANNOTATIONS_KEY(jerryCmpId) });
      queryClient.invalidateQueries({ queryKey: VD_COMPARISON_KEY(jerryCmpId) });
    },
  });
}

// ---------------------------------------------------------------------------
// AI document review (jerry-owned findings — independent of Veri Doc compare)
// ---------------------------------------------------------------------------

const AI_FINDINGS_KEY = (documentId: string) =>
  ["ai-findings", documentId] as const;

/** Fetch the AI-review findings for a document.
 *
 * W1-2: while the backend's async run is in flight (verdict
 * ``review_running``), poll every 3 s until a terminal report (including
 * W1-1's ``review_failed``) overwrites the marker. */
export function useDocumentAiFindings(documentId: string | null | undefined) {
  return useQuery({
    queryKey: AI_FINDINGS_KEY(documentId ?? ""),
    queryFn: () =>
      aiReviewApi.listFindings(documentId as string).then((r) => r.data),
    enabled: !!documentId,
    refetchInterval: (query) =>
      query.state.data?.review?.verdict === "review_running" ? 3000 : false,
  });
}

/** Trigger one AI review of a document; refreshes the findings list on success. */
export function useTriggerAiReview(documentId: string) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars?: { stance?: string }) =>
      aiReviewApi.trigger(documentId, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_FINDINGS_KEY(documentId) });
    },
    onError: (err) => {
      message.error(isApiError(err) ? err.message : "AI 审查发起失败，请重试");
    },
  });
}

/**
 * 对一条发现表态（采纳 / 不认同 / 撤销）。表态持久化在服务端，卡片受控于查询
 * 数据（不用本地 useState 假状态，刷新即失效的问题随之消失）。
 *
 * 否决分类与理由选填，且只在 processed="rejected" 时随请求发出——律师点「不认同」
 * 后即完成否决，随后补分类或理由是第二次调用，不阻塞第一次。
 */
export function useUpdateFindingProcessed(documentId: string) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      findingId: string;
      processed: AiFindingProcessed;
      reasonCode?: AiVerdictReasonCode | null;
      reasonText?: string | null;
    }) =>
      aiReviewApi
        .updateFinding(documentId, vars.findingId, {
          processed: vars.processed,
          ...(vars.processed === "rejected"
            ? { reasonCode: vars.reasonCode ?? null, reasonText: vars.reasonText ?? null }
            : {}),
        })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_FINDINGS_KEY(documentId) });
    },
    onError: (err) => {
      message.error(isApiError(err) ? err.message : "表态保存失败，请重试");
    },
  });
}

// ---------------------------------------------------------------------------
// D4：草稿自审查（写完即审）——三端点镜像上面的文档侧 ai-review/ai-findings，
// 权限收在草稿创建者写权限口径，不放宽到事项成员。
// ---------------------------------------------------------------------------

const DRAFT_AI_FINDINGS_KEY = (draftDocId: string) =>
  ["draft-ai-findings", draftDocId] as const;

/** 读取草稿自审查的最新结果；W1-2 同款轮询：review_running 期间每 3 秒轮询
 * 一次，直至终态报告（含失败）覆盖标记。 */
export function useDraftAiFindings(draftDocId: string | null | undefined) {
  return useQuery({
    queryKey: DRAFT_AI_FINDINGS_KEY(draftDocId ?? ""),
    queryFn: () =>
      draftDocsApi.listAiFindings(draftDocId as string).then((r) => r.data),
    enabled: !!draftDocId,
    refetchInterval: (query) =>
      query.state.data?.review?.verdict === "review_running" ? 3000 : false,
  });
}

/** 对草稿当前正文发起一次 AI 自审查；成功提交（202）后刷新发现列表以拿到
 * running 标记，随后由上面的轮询接管直至终态。 */
export function useTriggerDraftAiReview(draftDocId: string) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars?: { stance?: string }) =>
      draftDocsApi.triggerAiReview(draftDocId, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DRAFT_AI_FINDINGS_KEY(draftDocId) });
    },
    onError: (err) => {
      message.error(isApiError(err) ? err.message : "AI 审查发起失败，请重试");
    },
  });
}

/** 对一条草稿自审查发现表态，语义与 `useUpdateFindingProcessed` 一致，落库
 * 端点不同。 */
export function useUpdateDraftFindingProcessed(draftDocId: string) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      findingId: string;
      processed: AiFindingProcessed;
      reasonCode?: AiVerdictReasonCode | null;
      reasonText?: string | null;
    }) =>
      draftDocsApi
        .updateAiFinding(draftDocId, vars.findingId, {
          processed: vars.processed,
          ...(vars.processed === "rejected"
            ? { reasonCode: vars.reasonCode ?? null, reasonText: vars.reasonText ?? null }
            : {}),
        })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DRAFT_AI_FINDINGS_KEY(draftDocId) });
    },
    onError: (err) => {
      message.error(isApiError(err) ? err.message : "表态保存失败，请重试");
    },
  });
}

/**
 * AF2：把审查意见另存为事项文档。正文由服务端汇总，请求不带正文。
 *
 * 成功后失效 documents 查询——另存出来的是一份正常的事项文档，文档中心与事项
 * 文档树应立即可见，不必手工刷新。
 */
export function useSaveAiReviewAsDocument(documentId: string) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars?: { documentName?: string; matterId?: string }) =>
      aiReviewApi.saveReportAsDocument(documentId, vars),
    onSuccess: () => {
      message.success("已另存为事项文档");
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (err) => {
      // 服务端已区分「尚未审查 / 审查进行中 / 上次失败 / 未归属事项」四种情形，
      // 各自的下一步动作不同，原样透出，不压成一句笼统失败。
      message.error(isApiError(err) ? err.message : "另存失败，请重试");
    },
  });
}

/**
 * 决策 2026-08-08 §4：基于原文另存为可编辑草稿。
 *
 * 调用方（ReviewCardList）负责成功后的页面跳转——跳转目标是新草稿的起草
 * 工作台，路由跳转属于页面编排，不放在 hook 内。引擎侧不可用的四种情形
 * 服务端已区分并以 409 可读文案下发，原样透出。
 */
/**
 * 1A-2：从文档列表「以此为底稿起草」——同一后端端点，文档 id 随调用传入，
 * 供一张列表里的任意一行使用。源文档无事项时创建无事项草稿。
 */
export function useOpenDocumentAsDraft() {
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (vars: { documentId: string; title?: string; matterId?: string }) =>
      aiReviewApi.saveAsDraftDoc(vars.documentId, {
        ...(vars.title ? { title: vars.title } : {}),
        ...(vars.matterId ? { matterId: vars.matterId } : {}),
      }),
    onSuccess: (resp) => {
      if (resp.sourceHasTables) {
        message.warning(SOURCE_HAS_TABLES_WARNING);
      }
    },
    onError: (err) => {
      message.error(isApiError(err) ? err.message : "打开为草稿失败，请重试");
    },
  });
}

export function useSaveDocumentAsDraftDoc(documentId: string) {
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (vars?: { title?: string; matterId?: string }) =>
      aiReviewApi.saveAsDraftDoc(documentId, vars),
    onSuccess: (resp) => {
      if (resp.sourceHasTables) {
        message.warning(SOURCE_HAS_TABLES_WARNING);
      }
    },
    onError: (err) => {
      message.error(isApiError(err) ? err.message : "另存为草稿失败，请重试");
    },
  });
}

// ---------------------------------------------------------------------------
// Document download (blob + progress)
// ---------------------------------------------------------------------------

/** Shape shared by the blob-download hooks below. */
interface TriggeredDownload {
  download: (id: string, filename: string) => Promise<void>;
  progress: number | null;
  isDownloading: boolean;
}

/**
 * Trigger a client-side document download with optional progress reporting.
 *
 * Returns:
 * - `download(documentId, filename)` — kicks off the download.
 * - `isDownloading` — true while a download is in flight.
 * - `progress` — 0..100 percent for large streamed files; null otherwise.
 *
 * Surfaces user-facing errors via Ant Design `message.error`, mapping 403/404
 * to Chinese descriptions and falling back to the server message otherwise.
 */
export function useDocumentDownload(): TriggeredDownload {
  return useBlobDownload(downloadDocument, {
    403: "无权限下载此文档",
    404: "文档不存在或已被删除",
  });
}

/**
 * Trigger a docx export download of a workbench draft's current body
 * (顶栏「导出」，GET /draft-docs/{id}/export)。Same UX contract as
 * {@link useDocumentDownload}.
 */
export function useDraftDocExportDownload(): TriggeredDownload {
  return useBlobDownload(downloadDraftDocExport, {
    403: "无权限导出此草稿",
    404: "草稿不存在或已被删除",
    409: "草稿正文为空，无法导出",
  });
}

/**
 * Trigger a docx export download of a workbench draft's 留痕版——当前正文与
 * 上一正文里程碑的全部差异，带 Word 原生修订标记
 * （GET /draft-docs/{id}/export-redline）。批次三②。
 *
 * 409 区分两种前置条件缺失：正文为空（与 clean 导出同一约束）、尚无历史
 * 里程碑（草稿从未经历过批次首次决策，没有可比较的基线）。
 */
export function useDraftDocExportRedlineDownload(): TriggeredDownload {
  return useBlobDownload(downloadDraftDocExportRedline, {
    403: "无权限导出此草稿",
    404: "草稿不存在或已被删除",
    // 409 有两种互斥业务原因，后端已用 error.code 区分（DraftDocNoMilestone /
    // DraftDocBodyEmpty），按 code 分支给出对应的下一步动作，不用一句话
    // 含糊两种情况。
    409: (err) =>
      err.code === "DRAFT_DOC_NO_MILESTONE"
        ? "草稿尚无历史里程碑，无法生成留痕版对比"
        : "草稿正文为空，无法生成留痕版",
    500: "留痕版生成失败，请稍后重试或联系管理员",
  });
}

/**
 * 导出某文档的 AI 审查意见（GET /documents/{id}/ai-review/export）。
 * UX 契约与 {@link useDocumentDownload} 一致。
 *
 * A3：`format` 决定导出格式（markdown / docx / xlsx），在 hook 实例化时固定；
 * 组件对每种格式各持有一个实例，下载状态互不串扰。
 *
 * 400 对应三种"还没有可导出的结论"：从未审查、审查进行中、上次运行失败。
 * 服务端已给出具体原因，这里不覆盖它——三者的下一步动作各不相同
 * （先发起审查 / 等待完成 / 重试审查），统一成一句话会把律师引向错误的操作。
 */
export function useAiReviewExportDownload(
  format: AiReviewExportFormat = "markdown",
): TriggeredDownload {
  const run = useCallback(
    (
      id: string,
      fallbackFilename: string,
      token: string,
      onProgress?: (received: number, total: number | null) => void,
      signal?: AbortSignal,
    ) =>
      downloadAiReviewExport(id, fallbackFilename, token, onProgress, signal, format),
    [format],
  );
  return useBlobDownload(run, {
    403: "无权限导出此文档的审查意见",
    404: "文档不存在或已被删除",
  });
}

/**
 * Download the redacted copy of a Jerry document (一键脱敏下载，
 * GET /documents/{id}/download-redacted)。Same UX contract as
 * {@link useDocumentDownload}; 409 maps to "脱敏尚未完成".
 */
export function useDownloadRedactedDocument(): TriggeredDownload {
  return useBlobDownload(downloadRedactedDocument, {
    403: "无权限下载此文档",
    404: "文档不存在或已被删除",
    409: "脱敏尚未完成，请稍后再试",
  });
}

/**
 * Error-message map for {@link useBlobDownload}. A status maps to either a
 * fixed string, or a function of the {@link DownloadError} for statuses that
 * carry more than one business reason (e.g. 409 covering both
 * `DRAFT_DOC_NO_MILESTONE` and `DRAFT_DOC_BODY_EMPTY`) — the function
 * branches on `err.code` and returns the reason-specific sentence.
 */
type BlobDownloadErrorMap = Record<number, string | ((err: DownloadError) => string)>;

/**
 * Shared blob-download pipeline: token from storage, supersede/unmount abort,
 * progress for streamed files, status-mapped error toasts.
 */
function useBlobDownload(
  run: (
    id: string,
    fallbackFilename: string,
    token: string,
    onProgress?: (received: number, total: number | null) => void,
    signal?: AbortSignal,
  ) => Promise<void>,
  errorMessageByStatus: BlobDownloadErrorMap,
): TriggeredDownload {
  const { message } = App.useApp();
  const [progress, setProgress] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  // Abort an in-flight download when a new one starts or the consumer unmounts.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const download = async (id: string, filename: string) => {
    const token =
      typeof localStorage !== "undefined"
        ? (localStorage.getItem(AUTH_TOKEN_KEY) ?? "")
        : "";
    // Supersede any download still running.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsDownloading(true);
    setProgress(null);
    try {
      await run(
        id,
        filename,
        token,
        (received, total) => {
          if (total) {
            setProgress(Math.min(100, Math.round((received / total) * 100)));
          }
        },
        controller.signal,
      );
      message.success("下载完成");
    } catch (e) {
      // Silent on caller-initiated cancellation (unmount / superseded).
      if (controller.signal.aborted || (e instanceof DOMException && e.name === "AbortError")) {
        return;
      }
      // Classify by HTTP status when available, falling back to the message.
      const status = e instanceof DownloadError ? e.status : 0;
      const raw = e instanceof Error ? e.message : "下载失败";
      const mapped = errorMessageByStatus[status];
      const resolved =
        typeof mapped === "function"
          ? mapped(e instanceof DownloadError ? e : new DownloadError(raw, status))
          : mapped;
      message.error(resolved ?? raw);
    } finally {
      // Only clear state if this controller is still the active one (a newer
      // download may have replaced it).
      if (abortRef.current === controller) {
        abortRef.current = null;
        setIsDownloading(false);
        setProgress(null);
      }
    }
  };

  return { download, progress, isDownloading };
}

// ---------------------------------------------------------------------------
// Current-user permissions (jerry.* namespace)
// ---------------------------------------------------------------------------

/**
 * Fetch and cache the current user's jerry.* roles and permission points.
 * Drives role-aware UI gating. The cache is held for the session; permission
 * changes are rare, so a long stale time avoids refetch churn.
 */
export function useMyPermissions() {
  return useQuery({
    queryKey: queryKeys.myPermissions,
    queryFn: () => permissionsApi.me().then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// AI Memory (US-G05，契约先行 — 见 types/memory.ts)
// ---------------------------------------------------------------------------

const MEMORY_KEY = ["ai-memory"] as const;
const MEMORY_CONFIG_KEY = ["ai-memory-config"] as const;

/** 部署级开关 — 决定 SettingsMemory 是否尝试拉列表（S5-17/2b）。变化极少，
 * 给较长 staleTime。 */
export function useMemoryConfig() {
  return useQuery({
    queryKey: MEMORY_CONFIG_KEY,
    queryFn: () => memoryApi.getConfig().then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });
}

/** `enabled` 由调用方传入 `injectionEnabled` 门控——功能关闭时不发起列表请求。 */
export function useMemoryList(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: MEMORY_KEY,
    queryFn: () => memoryApi.list().then((res) => res.data.items),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MemoryCreateRequest) => memoryApi.create(data).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMORY_KEY });
    },
  });
}

/** 后端把 `source` 置为 manual，即便原条目是 distillation 提炼所得（S5-03）。 */
export function useUpdateMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MemoryUpdateRequest }) =>
      memoryApi.update(id, data).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMORY_KEY });
    },
  });
}

export function useDeleteMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => memoryApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMORY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Conflict-of-interest checks (利益冲突核查)
// ---------------------------------------------------------------------------

export function useConflictChecks(params?: {
  matterId?: string;
  clientId?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: queryKeys.conflictChecks(
      params as Record<string, unknown> | undefined,
    ),
    queryFn: () => conflictChecksApi.list(params),
    enabled: !!(params?.matterId || params?.clientId),
  });
}

export function useConflictCheckDetail(id: string) {
  return useQuery({
    queryKey: queryKeys.conflictCheck(id),
    queryFn: () => conflictChecksApi.get(id).then((res) => res.data),
    enabled: !!id,
  });
}

export function useCreateConflictCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ConflictCheckCreateRequest) =>
      conflictChecksApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conflict-checks"] });
    },
  });
}

export function useUpdateConflictCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConflictCheckUpdateRequest }) =>
      conflictChecksApi.update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["conflict-checks"] });
      qc.invalidateQueries({ queryKey: ["conflict-checks", id] });
    },
  });
}

export function useChangeConflictCheckStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: ConflictCheckStatusChangeRequest;
    }) => conflictChecksApi.changeStatus(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["conflict-checks"] });
      qc.invalidateQueries({ queryKey: ["conflict-checks", id] });
    },
  });
}

export function useDeleteConflictCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conflictChecksApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conflict-checks"] });
    },
  });
}

// ---------------------------------------------------------------------------
// AI drafting (US-D「AI 真实起草」)
// ---------------------------------------------------------------------------

/** Polling cadence while a draft is pending/running (mirrors the pipeline-instance convention). */
const DRAFT_POLL_INTERVAL_MS = 5000;

/** A matter's drafting records, most recent first. Polls while any listed record is non-terminal. */
export function useDrafts(matterId: string, params?: { page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: queryKeys.drafts(matterId, params as Record<string, unknown> | undefined),
    queryFn: () => draftsApi.list(matterId, params),
    enabled: !!matterId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.data) return false;
      const hasActive = data.data.some((d) =>
        DRAFT_NON_TERMINAL_STATUSES.includes(d.status),
      );
      return hasActive ? DRAFT_POLL_INTERVAL_MS : false;
    },
  });
}

/** Single drafting record — initial load + generation-in-progress polling (US-D01 6). */
export function useDraft(draftId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.draft(draftId ?? ""),
    queryFn: () => draftsApi.get(draftId as string).then((res) => res.data),
    enabled: !!draftId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return DRAFT_NON_TERMINAL_STATUSES.includes(data.status) ? DRAFT_POLL_INTERVAL_MS : false;
    },
  });
}

/** Create (and asynchronously run) a draft; returns the "pending" record immediately. */
export function useCreateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matterId, data }: { matterId: string; data: DraftCreateRequest }) =>
      draftsApi.create(matterId, data).then((res) => res.data),
    onSuccess: (_draft, { matterId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.drafts(matterId) });
    },
  });
}

/** Cancel a still-generating draft (US-D01 6b; 409 if it already reached a terminal state). */
export function useCancelDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) => draftsApi.cancel(draftId).then((res) => res.data),
    onSuccess: (draft) => {
      qc.invalidateQueries({ queryKey: queryKeys.draft(draft.id) });
      qc.invalidateQueries({ queryKey: ["jerry-drafts"] });
    },
  });
}

/**
 * Save the (possibly hand-edited) body as a matter document (US-D02; 409 unless the
 * draft's status is "succeeded"). Invalidates the draft (picks up savedDocumentId)
 * and the matter's document list (the new document must appear without a reload).
 */
export function useSaveDraftAsDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, data }: { draftId: string; data: DraftSaveAsDocumentRequest }) =>
      draftsApi.saveAsDocument(draftId, data).then((res) => res.data),
    onSuccess: (_document, { draftId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.draft(draftId) });
      qc.invalidateQueries({ queryKey: ["jerry-drafts"] });
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Workbench drafts (`jerry_draft_docs`, /draft-docs) — 对话式起草工作台草稿
// ---------------------------------------------------------------------------

/** True when the draft-doc list response contains a row whose 一键成稿
 * 仍在进行中（oneshotStatus 为非终态）。用作 refetchInterval 谓词，令
 * 草稿箱徽标（生成中/失败）不必手工刷新即可自行收敛到终态，写法与
 * useDocuments 的 hasPendingRagDocs 同一口径（见本文件上方）。 */
function hasPendingOneshotDrafts(data: unknown): boolean {
  const rows = (data as { data?: { oneshotStatus?: string | null }[] } | undefined)?.data;
  return Array.isArray(rows)
    ? rows.some((r) =>
        DRAFT_NON_TERMINAL_STATUSES.includes(r?.oneshotStatus as (typeof DRAFT_NON_TERMINAL_STATUSES)[number]),
      )
    : false;
}

/** 草稿箱列表——当前用户可见的全部工作台草稿，服务端筛选与分页。 */
export function useDraftDocs(params?: DraftDocListParams) {
  return useQuery({
    queryKey: queryKeys.draftDocs(params as Record<string, unknown> | undefined),
    queryFn: () => draftDocsApi.list(params),
    refetchInterval: (query) =>
      hasPendingOneshotDrafts(query.state.data) ? RAG_PENDING_POLL_MS : false,
  });
}

/** 单份工作台草稿（起草工作台初始加载）。 */
export function useDraftDoc(draftDocId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.draftDoc(draftDocId ?? ""),
    queryFn: () => draftDocsApi.get(draftDocId as string).then((res) => res.data),
    enabled: !!draftDocId,
  });
}

/** 新建工作台草稿；成功后调用方跳转 `/drafts/{id}` 进入工作台。 */
export function useCreateDraftDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DraftDocCreateRequest) =>
      draftDocsApi.create(data).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["draft-docs"] });
    },
  });
}

/** 更新草稿元数据（标题/文书类型/状态/事项绑定）。 */
export function useUpdateDraftDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftDocId, data }: { draftDocId: string; data: DraftDocUpdateRequest }) =>
      draftDocsApi.update(draftDocId, data).then((res) => res.data),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: queryKeys.draftDoc(doc.id) });
      qc.invalidateQueries({ queryKey: ["draft-docs"] });
    },
  });
}

/**
 * 人工编辑保存草稿正文（PUT body；不产生版本行）。resolved 值在
 * DraftDoc 字段之外附带 `anchorsAccepted`（D7，阶段二）——响应顶层字段，
 * 与 `success`/`data` 同级，体现本次随正文上报的锚点是否通过服务端重算
 * 校验，供保存链决定是否 console.warn（不影响正文保存本身的成败）。
 */
export function useUpdateDraftDocBody() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftDocId, data }: { draftDocId: string; data: DraftDocBodyUpdateRequest }) =>
      draftDocsApi
        .updateBody(draftDocId, data)
        .then((res) => ({ ...res.data, anchorsAccepted: res.anchorsAccepted })),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: queryKeys.draftDoc(doc.id) });
      qc.invalidateQueries({ queryKey: ["draft-docs"] });
    },
  });
}

/**
 * AI 轮次快照列表（左栏版本回看）。服务端按轮次倒序返回（最新在前），
 * 单页上限 100：取第一页即最近 100 轮，更早轮次不再展示。渲染方按返回
 * 顺序展示，轮次标签取每项自带的 roundNo，不依赖数组顺序推断。
 */
export function useDraftDocVersions(draftDocId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.draftDocVersions(draftDocId ?? ""),
    queryFn: () => draftDocsApi.versions(draftDocId as string, { pageSize: 100 }),
    enabled: !!draftDocId,
  });
}

/**
 * D1 阶段三（提案层，ADR 2026-08-16 方案 C）：提案批次列表——权威渲染数据
 * 源（含 `baselineAnchors`，SSE `document.proposals` 精简帧不携带）。右栏
 * 提案面板与顶部待决计数条共用同一份查询结果，不重复请求。单页上限 100：
 * 拍板 4 长期留档，早期批次量级下足够覆盖；后续批次量增长后再补分页 UI。
 */
export function useDraftDocProposals(draftDocId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.draftDocProposals(draftDocId ?? ""),
    queryFn: () => draftDocsApi.listProposals(draftDocId as string, { pageSize: 100 }),
    enabled: !!draftDocId,
  });
}

/** 单条提案决策（采纳/拒绝）。调用方须先完成编辑器替换 + 正文保存，成功后
 * 才调用本 mutation（ADR §六严格顺序，页面层 orchestrate，本 hook 不隐含
 * 该顺序）。成功后刷新提案列表与版本列表（批次首次决策落地前落一条版本
 * 行）；不刷新 draftDoc——本端点不写正文，正文写入经既有 body mutation 的
 * onSuccess 单独刷新。 */
export function useDecideDraftProposal(draftDocId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { proposalId: string; decision: DraftProposalDecisionRequest["decision"] }) =>
      draftDocsApi
        .decideProposal(draftDocId, vars.proposalId, { decision: vars.decision })
        .then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.draftDocProposals(draftDocId) });
      qc.invalidateQueries({ queryKey: queryKeys.draftDocVersions(draftDocId) });
    },
  });
}

/** 整批采纳/拒绝。整批采纳时服务端直接把批次结果正文写入 `current_body`
 * （不逐条重放），成功后一并刷新草稿本体（正文/pendingProposalCount 相关
 * 显示）与版本列表；调用方从返回的 `draftDoc.currentBody` 回填编辑器。 */
export function useDecideDraftProposalBatch(draftDocId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { batchId: string; decision: DraftProposalBatchDecisionRequest["decision"] }) =>
      draftDocsApi
        .decideProposalBatch(draftDocId, vars.batchId, { decision: vars.decision })
        .then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.draftDocProposals(draftDocId) });
      qc.invalidateQueries({ queryKey: queryKeys.draftDoc(draftDocId) });
      qc.invalidateQueries({ queryKey: queryKeys.draftDocVersions(draftDocId) });
    },
  });
}

/**
 * 一键成稿：返回一次性起草记录（jerry_drafts），进度由调用方以
 * {@link useDraft} 轮询至终态；成功后草稿正文由后台任务回填，调用方
 * 在终态处 invalidate draftDoc 拉取新正文。
 */
export function useStartDraftDocOneShot() {
  return useMutation({
    mutationFn: ({ draftDocId, instructions }: { draftDocId: string; instructions?: string }) =>
      draftDocsApi.oneShot(draftDocId, { instructions }).then((res) => res.data),
  });
}

/**
 * 另存为事项文档（阶段 6）。成功后 invalidate 草稿本体（拿到
 * savedDocumentId 与可能新绑定的事项）、草稿箱列表与文档列表（新文档
 * 需要免刷新出现在事项文档里）。
 */
export function useSaveDraftDocAsDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftDocId, data }: { draftDocId: string; data: DraftDocSaveAsDocumentRequest }) =>
      draftDocsApi.saveAsDocument(draftDocId, data).then((res) => res.data),
    onSuccess: (_document, { draftDocId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.draftDoc(draftDocId) });
      qc.invalidateQueries({ queryKey: ["draft-docs"] });
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

/** 软删除草稿（仅创建者）；列表随之刷新。 */
export function useDeleteDraftDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftDocId: string) =>
      draftDocsApi.remove(draftDocId).then(() => draftDocId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["draft-docs"] });
    },
  });
}

// ---------------------------------------------------------------------------
// System LLM settings (admin only)
// ---------------------------------------------------------------------------

const LLM_SETTINGS_KEY = ["llm-settings"] as const;

/** Current engine LLM config (masked key). Admin-only; 403 for other roles. */
export function useLLMSettings(enabled = true) {
  return useQuery({
    queryKey: LLM_SETTINGS_KEY,
    queryFn: () => llmSettingsApi.get().then((res) => res.data),
    enabled,
    staleTime: 30_000,
  });
}

/** Built-in provider presets used to prefill the form. */
export function useLLMPresets(enabled = true) {
  return useQuery({
    queryKey: ["llm-presets"],
    queryFn: () => llmSettingsApi.presets().then((res) => res.data),
    enabled,
    staleTime: Infinity,
  });
}

/** Persist the engine LLM config; refetches the masked read on success. */
export function useUpdateLLMSettings() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LLMSettingsUpdatePayload) =>
      llmSettingsApi.update(payload).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LLM_SETTINGS_KEY });
      message.success("大语言模型设置已保存并生效");
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : "保存失败");
    },
  });
}

/** Probe a candidate config without persisting. Returns { success, message }. */
export function useTestLLMConnection() {
  return useMutation({
    mutationFn: (payload: LLMSettingsUpdatePayload) =>
      llmSettingsApi.test(payload).then((res) => res.data),
  });
}

// ---------------------------------------------------------------------------
// Meetings (会议助手) — P0 baseline. Sync / skill triggers / OAuth land later.
// ---------------------------------------------------------------------------

export function useMeetings(params?: MeetingListParams) {
  return useQuery({
    queryKey: queryKeys.meetings(params as Record<string, unknown> | undefined),
    queryFn: () => meetingsApi.list(params),
  });
}

export function useMeetingDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.meeting(id ?? ""),
    queryFn: () => meetingsApi.get(id as string).then((res) => res.data),
    enabled: !!id,
  });
}

export function useCreateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MeetingCreateRequest) => meetingsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
    },
  });
}

/** 在会议平台上预订会议（显式写操作）。 */
export function useBookMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MeetingBookPayload) => meetingsApi.book(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
    },
  });
}

export function useUpdateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MeetingUpdateRequest }) =>
      meetingsApi.update(id, data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings", vars.id] });
    },
  });
}

export function useDeleteMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
    },
  });
}

export function useImportMeetingTranscript() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MeetingTranscriptImportRequest }) =>
      meetingsApi.importTranscript(id, data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings", vars.id] });
    },
  });
}

/**
 * Upload audio for a meeting (recorder-pen capture). On success invalidates
 * the meeting list + detail so consumers see the new audioStorageKey.
 */
export function useUploadMeetingAudio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      file,
      options,
    }: {
      id: string;
      file: File;
      options?: { durationSeconds?: number; recordedAt?: string };
    }) => meetingsApi.uploadAudio(id, file, options).then((res) => res.data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings", vars.id] });
    },
  });
}

/**
 * Fetch a presigned audio URL on demand. Disabled when `active=false` so
 * callers can defer the fetch until the user opens the player.
 */
export function useMeetingAudioUrl(id: string | null | undefined, active = true) {
  return useQuery({
    queryKey: ["meetings", id ?? "", "audio-url"],
    queryFn: () => meetingsApi.getAudioUrl(id as string).then((res) => res.data),
    enabled: !!id && active,
  });
}

/** Remove audio attachment from a meeting (storage + fields). */
export function useDeleteMeetingAudio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.deleteAudio(id).then((res) => res.data),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings", id] });
    },
  });
}

/**
 * Trigger ASR transcription on a meeting's audio. Returns the Celery task
 * id; on success invalidates the meeting list + detail so consumers see
 * the new transcriptStatus === "pending".
 */
export function useTriggerMeetingTranscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hotwords }: { id: string; hotwords?: string[] }) =>
      meetingsApi.transcribe(id, hotwords).then((res) => res.data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings", vars.id] });
      qc.invalidateQueries({ queryKey: ["meetings", vars.id, "transcript"] });
    },
  });
}

/**
 * Poll a meeting's transcript state. Refetches every 3s while status ===
 * "pending"; stops once the terminal state ("ready" / "failed" / "none") is
 * reached. Pass `active=false` to defer the fetch until the user opens the
 * player. Mirrors `useConversationAttachmentStatus`'s polling pattern.
 */
export function useMeetingTranscript(
  id: string | null | undefined,
  active = true,
) {
  return useQuery({
    queryKey: ["meetings", id ?? "", "transcript"],
    queryFn: () => meetingsApi.getTranscript(id as string).then((res) => res.data),
    enabled: !!id && active,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "pending" ? 3000 : false;
    },
  });
}

/**
 * Import an existing audio file (vs live recording) and auto-trigger ASR.
 * Combines upload + transcribe in one server-side call. Used by the
 * recorder-pen's "导入音频文件" button.
 */
export function useImportMeetingAudio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      file,
      options,
    }: {
      id: string;
      file: File;
      options?: {
        durationSeconds?: number;
        recordedAt?: string;
        autoTranscribe?: boolean;
      };
    }) => meetingsApi.importAudio(id, file, options).then((res) => res.data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings", vars.id] });
      qc.invalidateQueries({ queryKey: ["meetings", vars.id, "transcript"] });
    },
  });
}

/**
 * 逐场导入会议内容（平台纪要或手工粘贴）。成功后刷新会议清单与详情，使会议由
 * 「未导入内容」翻到「已导入内容」。
 */
export function useImportMeetingContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MeetingContentImportRequest }) =>
      meetingsApi.importContent(id, data).then((res) => res.data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings", vars.id] });
    },
  });
}

/** 归档会议纪要。成功后刷新会议详情，归档入口切成「查看纪要文档」。 */
export function useArchiveMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.archive(id).then((res) => res.data),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings", id] });
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

/**
 * 本人确认纪要（S11）。确认后待办确认、归档解锁；同一会议重复确认或状态
 * 不满足前置条件返回 409——调用方 catch 后据 err.status 展示提示，成功与
 * 失败都要刷新会议详情，使界面与服务端状态保持一致。
 */
export function useConfirmMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.confirm(id).then((res) => res.data),
    onSettled: (_res, _err, id) => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings", id] });
    },
  });
}

/** 撤销本人确认。已归档的会议后端拒绝撤销，调用方按 err.status 展示原因。 */
export function useRevokeMeetingConfirmation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.revokeConfirmation(id).then((res) => res.data),
    onSettled: (_res, _err, id) => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings", id] });
    },
  });
}

/** 说话人批量改名。成功后刷新逐字稿，全部同标签分段的说话人一并更新。 */
export function useRenameTranscriptSpeaker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: MeetingTranscriptSpeakerRenameRequest;
    }) => meetingsApi.renameTranscriptSpeaker(id, data).then((res) => res.data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["meetings", vars.id, "transcript"] });
      // 会议详情上冗余存了一份 transcriptText；不刷新它就会在改名后继续显示
      // 改名前的说话人标签，与逐字稿页签的最新分段对不上。
      qc.invalidateQueries({ queryKey: ["meetings", vars.id] });
    },
  });
}

/** 逐字稿分段编辑（改文字 / 改单段说话人 / 切换高亮）。 */
export function useUpdateTranscriptSegments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: MeetingTranscriptSegmentsEditRequest;
    }) => meetingsApi.updateTranscriptSegments(id, data).then((res) => res.data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["meetings", vars.id, "transcript"] });
      // 同上：会议详情上的 transcriptText 是分段拼接前的旧文本，编辑分段后
      // 一并刷新，避免逐字稿页签的纯文本兜底路径读到过期正文。
      qc.invalidateQueries({ queryKey: ["meetings", vars.id] });
    },
  });
}

/**
 * 提交一次会议 AI 产出（会后总结 / 待办抽取 / 会前准备）。
 *
 * 提交后立即返回，实际产出在后台完成，由 {@link useMeetingAiActionState} 轮询。
 * 三个动作各提交各的，一个在途不阻止另外两个。
 */
export function useSubmitMeetingAiAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: MeetingAiAction }) =>
      meetingsApi.submitAction(id, action).then((res) => res.data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["meetings", vars.id] });
      qc.invalidateQueries({
        queryKey: ["meetings", vars.id, "actions", vars.action],
      });
    },
  });
}

/**
 * 轮询一个动作的产出状态。
 *
 * 终止条件三条，任一成立即停：状态离开「生成中」（已就绪、失败或从未生成）；
 * 连续处于「生成中」超过 {@link MEETING_AI_POLL_MAX_MS}；组件卸载或 `active`
 * 转为 false。
 *
 * 状态离开「生成中」时刷新会议详情与候选待办：产出落在会议行与任务表上，界面读的
 * 是那两处而不是本响应的 `result`——这样一来，失败时上一次的产出仍然照常展示。
 */
export function useMeetingAiActionState(
  meetingId: string | null | undefined,
  action: MeetingAiAction,
  active = true,
) {
  const qc = useQueryClient();
  const pollStartedAtRef = useRef<number | null>(null);
  const previousStateRef = useRef<string | undefined>(undefined);

  const query = useQuery({
    queryKey: ["meetings", meetingId ?? "", "actions", action] as const,
    queryFn: () =>
      meetingsApi.getActionState(meetingId as string, action).then((res) => res.data),
    enabled: !!meetingId && active,
    refetchInterval: (q) => {
      const state = q.state.data?.state;
      const now = Date.now();
      if (state !== "pending") {
        pollStartedAtRef.current = null;
      } else if (pollStartedAtRef.current === null) {
        pollStartedAtRef.current = now;
      }
      return meetingAiPollInterval(state, pollStartedAtRef.current, now);
    },
  });

  const state = query.data?.state;
  useEffect(() => {
    const previous = previousStateRef.current;
    previousStateRef.current = state;
    if (previous !== "pending" || state === "pending" || !meetingId) return;
    qc.invalidateQueries({ queryKey: ["meetings", meetingId] });
    if (action === "extract-tasks") {
      qc.invalidateQueries({ queryKey: ["meetings", meetingId, "task-candidates"] });
    }
  }, [state, meetingId, action, qc]);

  return query;
}

/** 本会议下尚未确认的候选待办。确认后列表清空。 */
export function useMeetingTaskCandidates(meetingId: string | null | undefined) {
  return useQuery({
    queryKey: ["meetings", meetingId ?? "", "task-candidates"] as const,
    queryFn: () =>
      meetingsApi.listTaskCandidates(meetingId as string).then((res) => res.data),
    enabled: !!meetingId,
  });
}

/**
 * 逐条确认候选待办。整批生效或整批不生效；成功后候选列表清空，保留的条目出现在
 * 正式任务列表里。
 */
export function useConfirmMeetingTaskCandidates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decisions,
    }: {
      id: string;
      decisions: MeetingTaskCandidateDecision[];
    }) => meetingsApi.confirmTaskCandidates(id, decisions).then((res) => res.data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["meetings", vars.id] });
      qc.invalidateQueries({ queryKey: ["meetings", vars.id, "task-candidates"] });
    },
  });
}

export function useMeetingTasks(meetingId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.tasks({ meetingId: meetingId ?? "" }),
    queryFn: () => tasksApi.list({ meetingId: meetingId as string, pageSize: 100 }),
    enabled: !!meetingId,
  });
}

// ---------------------------------------------------------------------------
// Due Diligence（尽职调查） — v1
// 参见 docs/decisions/2026-07-09-尽职调查-v1-决策.md
// ---------------------------------------------------------------------------

/** 尽调项目列表（跨事项聚合当前用户可见项目）。 */
export function useDueDiligenceProjects(params?: DueDiligenceProjectListParams) {
  return useQuery({
    queryKey: queryKeys.ddProjects(params as Record<string, unknown> | undefined),
    queryFn: () => dueDiligenceProjectsApi.list(params),
  });
}

/** 单个尽调项目详情（含 subReports 与 materials）。 */
export function useDueDiligenceProject(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.ddProject(projectId ?? ""),
    queryFn: () =>
      dueDiligenceProjectsApi.get(projectId as string).then((res) => res.data),
    enabled: !!projectId,
  });
}

/** 1A-3：从文档库关联既有文档为尽调资料。成功后刷新项目详情（materials）。 */
export function useAttachDDMaterials(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentIds: string[]) =>
      dueDiligenceProjectsApi.attachMaterials(projectId, documentIds).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.ddProject(projectId) });
    },
  });
}

/** 1A-3：解除一份资料与尽调项目的关联。 */
export function useDetachDDMaterial(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      dueDiligenceProjectsApi.detachMaterial(projectId, documentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.ddProject(projectId) });
    },
  });
}

/** 调用方的个人空间文档（无事项）。供资料关联、参考资料等选择器列出
 *  "我自己上传的、还没挂到事项的文档"。 */
export function usePersonalDocuments(enabled = true) {
  return useQuery({
    queryKey: ["personal-documents", { scope: "personal", forPicker: true }],
    queryFn: () => documentsApi.list({ scope: "personal", pageSize: 100 }),
    enabled,
    refetchInterval: (query) =>
      hasPendingRagDocs(query.state.data) ? RAG_PENDING_POLL_MS : false,
  });
}

/** 新建尽调项目；成功后调用方跳转 `/due-diligence/{id}`。 */
export function useCreateDueDiligenceProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DueDiligenceProjectCreatePayload) =>
      dueDiligenceProjectsApi.create(data).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["due-diligence"] });
    },
  });
}

/** 更新项目元数据或取消（status=cancelled 仅 dispatched/collecting 可取消）。 */
export function useUpdateDueDiligenceProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string;
      data: DueDiligenceProjectUpdatePayload;
    }) => dueDiligenceProjectsApi.update(projectId, data).then((res) => res.data),
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: queryKeys.ddProject(project.id) });
      qc.invalidateQueries({ queryKey: ["due-diligence"] });
    },
  });
}

/** 软删除项目（仅 draft/cancelled/failed 状态可删）。 */
export function useDeleteDueDiligenceProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      dueDiligenceProjectsApi.remove(projectId).then(() => projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["due-diligence"] });
    },
  });
}

/** 列项目下全部子报告（v1 每项目 1 行；用于 SSE 断线后轮询对账）。 */
export function useDueDiligenceSubReports(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.ddSubReports(projectId ?? ""),
    queryFn: () =>
      dueDiligenceProjectsApi
        .listSubReports(projectId as string)
        .then((res) => res.data),
    enabled: !!projectId,
  });
}

/**
 * 信息收集会话历史（引擎持久化；draft 状态返回空）。供收集阶段初始化对话流。
 * `refetchInterval` 仅在传入 `polling=true` 时启用——收集阶段不需要轮询，
 * 调研阶段靠项目级 SSE 推送，断线兜底由 useDueDiligenceSubReports 完成。
 */
export function useDDProjectMessages(
  projectId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.ddProjectMessages(projectId ?? ""),
    queryFn: () =>
      dueDiligenceProjectsApi.listMessages(projectId as string),
    enabled: !!projectId && (options?.enabled ?? true),
  });
}

/**
 * 启动调研（v1 单路直派）。返回新建 sub_report（status=pending）。
 * 成功后失效项目详情与子报告列表，调用方触发 SSE 订阅。
 */
export function useDispatchDDResearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      dueDiligenceProjectsApi.dispatch(projectId),
    onSuccess: (_sub, projectId) => {
      qc.invalidateQueries({ queryKey: queryKeys.ddProject(projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.ddSubReports(projectId) });
    },
  });
}

/** 失败重试——产生新 sub_report 行（retry_of_id 血缘），重新派发。 */
export function useRetryDDSubReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      subReportId,
    }: {
      projectId: string;
      subReportId: string;
    }) => dueDiligenceProjectsApi.retrySubReport(projectId, subReportId),
    onSuccess: (_sub, { projectId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.ddProject(projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.ddSubReports(projectId) });
    },
  });
}

/**
 * 已完成项目重新调查（仅 PIR + completed 状态可用）。
 * body.research_question 不传沿用原值；传新值则更新后派发。
 * 成功后失效 project + sub_reports query，触发 SSE 订阅重新拉取。
 */
export function useReResearchPublicResearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      researchQuestion,
    }: {
      projectId: string;
      researchQuestion?: string | null;
    }) =>
      dueDiligenceProjectsApi.reResearch(projectId, {
        research_question: researchQuestion,
      }),
    onSuccess: (_sub, { projectId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.ddProject(projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.ddSubReports(projectId) });
    },
  });
}

/**
 * 导出子报告 Markdown。封装为 hook 以共享错误处理 + 下载触发；返回
 * `download(projectId, subId, filename)` 触发浏览器下载。
 */
export function useExportDDSubReport() {
  const { message } = App.useApp();
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async (
    projectId: string,
    subReportId: string,
    filename: string,
  ) => {
    setIsDownloading(true);
    setError(null);
    try {
      const blob = await dueDiligenceProjectsApi.exportSubReport(
        projectId,
        subReportId,
      );
      const url = URL.createObjectURL(
        new Blob([blob], { type: "text/markdown; charset=utf-8" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = filename.endsWith(".md") ? filename : `${filename}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = isApiError(err) ? err.message : "导出失败";
      setError(msg);
      void message.error(msg);
    } finally {
      setIsDownloading(false);
    }
  };

  return { download, isDownloading, error };
}

/** 把子报告正文另存为事项文档（复用 US-D02 落库链路）。 */
export function useSaveDDSubReportAsDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      subReportId,
      data,
    }: {
      projectId: string;
      subReportId: string;
      data: DueDiligenceSubReportSaveAsDocumentPayload;
    }) =>
      dueDiligenceProjectsApi.saveSubReportAsDocument(
        projectId,
        subReportId,
        data,
      ),
    onSuccess: (doc, { projectId }) => {
      // 另存产生新的事项文档；失效相关文档列表。sub_report 本身不变。
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: queryKeys.ddProject(projectId) });
      void doc;
    },
  });
}

/**
 * 上传尽调资料附件。无独立后端端点——附件进入 `jerry_documents` 表，前端
 * 把生成的 documentId 经 `POST /messages` 的 `referenceDocumentIds` 关联到
 * 项目（后端登记到 `jerry_due_diligence_materials`）。
 *
 * 归属：已绑定事项的项目上传到该事项（ownerScope=matter）；未绑定事项时
 * 上传到个人空间（ownerScope=personal），避免资料散落在 tenant 全局。
 */
export function useUploadDDAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      title,
      matterId,
    }: {
      file: File;
      title?: string;
      matterId: string | null;
    }) =>
      documentsApi.uploadWithScope({
        file,
        title: title ?? file.name,
        type: "attachment",
        ownerScope: matterId ? "matter" : "personal",
        ...(matterId ? { ownerScopeId: matterId, matterId } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["workbench-documents"] });
    },
  });
}

// ---------------------------------------------------------------------------
// 模式 B：公开信息调研（public-intel-research v1）—— 平行新增 hooks，
// 不修改任何模式 A hook（RC-2）。参见 docs/iterations/public-intel-research-v1-plan.md
// 阶段 5
// ---------------------------------------------------------------------------

/**
 * 启动公开调研（模式 B）。返回新建 sub_report（status=pending）。
 *
 * 与模式 A `useDispatchDDResearch` 镜像：成功后失效项目详情与子报告列表，
 * 调用方触发 SSE 订阅。模式 B 调用 `dispatch-public` 端点；模式 A 项目调用
 * 本 hook 会被 service 层拒绝（409/400）。
 */
export function useDispatchPublicResearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      dueDiligenceProjectsApi.dispatchPublicResearch(projectId),
    onSuccess: (_sub, projectId) => {
      qc.invalidateQueries({ queryKey: queryKeys.ddProject(projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.ddSubReports(projectId) });
    },
  });
}

/**
 * 公开调研来源引用列表（按 source_type / credibility 可选筛选）。
 *
 * 用于报告阶段右栏 SourceList 渲染。失败时由调用方处理错误态。
 */
export function usePublicResearchSources(
  projectId: string | undefined,
  params?: PublicResearchSourcesParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.ddPublicResearchSources(
      projectId ?? "",
      params as Record<string, unknown> | undefined,
    ),
    queryFn: () =>
      dueDiligenceProjectsApi.listPublicResearchSources(
        projectId as string,
        params,
      ),
    enabled: !!projectId && (options?.enabled ?? true),
  });
}

/**
 * 公开调研任务执行日志（v1 占位；UI 通过 SSE 暴露生命周期，本 hook 预留给
 * v2.1 调试面板展示 job 级查询规划/工具调用明细）。
 */
export function usePublicResearchJob(
  _projectId: string | undefined,
  _options?: { enabled?: boolean },
) {
  return { data: null as null };
}

// ---------------------------------------------------------------------------
// Calendar (底座 calendar 模块 — 日历页 CalendarEvent 主体)
//
// 数据源：GET /api/v1/calendar/*。响应字段在 api/calendarApi 内部已转 camelCase。
// 与 useCompass() 并存：compass 提供 task+deadline，calendar 提供 events。
// ---------------------------------------------------------------------------

export function useCalendars() {
  return useQuery({
    queryKey: ["calendar", "calendars"],
    queryFn: () => calendarApi.listCalendars(),
  });
}

/**
 * 首次访问 /calendar 时确保用户至少有一个 default calendar。
 * 后端没有 ensure_default 钩子，前端显式创建：发现 list 为空就 POST 一个。
 * 多标签页竞态由后端 is_default 唯一约束兜底（重复创建失败 → 重新拉取）。 */
export function useEnsureDefaultCalendar() {
  const qc = useQueryClient();
  const utils = useMutation({
    mutationFn: async (): Promise<Calendar> => {
      const list = await calendarApi.listCalendars();
      if (list.length > 0) return list[0]!;
      return calendarApi.createCalendar({
        name: "我的日历",
        visibility: "private",
        isDefault: true,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar", "calendars"] });
    },
  });
  return utils;
}

export function useEvents(params: {
  from: string;
  to: string;
  calendarId?: string;
  eventType?: string;
}) {
  return useQuery({
    queryKey: ["calendar", "events", params],
    queryFn: () => calendarApi.listEvents(params),
  });
}

export function useEventDetail(eventId: string | null | undefined) {
  return useQuery({
    queryKey: ["calendar", "event", eventId],
    queryFn: () => calendarApi.getEvent(eventId as string),
    enabled: !!eventId,
  });
}

export function useEventTypes() {
  return useQuery({
    queryKey: ["calendar", "event-types"],
    queryFn: () => calendarApi.listEventTypes(),
    staleTime: 5 * 60 * 1000, // 全局注册表，5 分钟缓存即可
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      payload,
      calendarId,
    }: {
      payload: Parameters<typeof calendarApi.createEvent>[0];
      calendarId: string;
    }) => calendarApi.createEvent(payload, calendarId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar", "events"] });
    },
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
      options,
    }: {
      id: string;
      payload: Parameters<typeof calendarApi.updateEvent>[1];
      options?: Parameters<typeof calendarApi.updateEvent>[2];
    }) => calendarApi.updateEvent(id, payload, options),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["calendar", "events"] });
      qc.invalidateQueries({ queryKey: ["calendar", "event", variables.id] });
    },
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      options,
    }: {
      id: string;
      options?: Parameters<typeof calendarApi.deleteEvent>[1];
    }) => calendarApi.deleteEvent(id, options),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar", "events"] });
    },
  });
}

export function useAddReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      payload,
    }: {
      eventId: string;
      payload: Parameters<typeof calendarApi.addReminder>[1];
    }) => calendarApi.addReminder(eventId, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["calendar", "event", variables.eventId],
      });
    },
  });
}

export function useDeleteReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      reminderId,
    }: {
      eventId: string;
      reminderId: string;
    }) => calendarApi.deleteReminder(eventId, reminderId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["calendar", "event", variables.eventId],
      });
    },
  });
}

export function useDeleteCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => calendarApi.deleteCalendar(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar", "calendars"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Redact — 一键脱敏 (Phase 2 Batch2)
//
// Backend:
//   POST /documents/{id}/redact          trigger (async, returns current status)
//   GET  /documents/{id}/redact-status    poll until ready/failed
//   GET  /documents/{id}/download-redacted  blob download
//
// Upload reuses documentsApi.uploadWithScope with personal scope so the
// workbench doesn't require a matter; a dedicated hook shields the page from
// the multipart plumbing.
// ---------------------------------------------------------------------------

/** Polling interval for redact-status while pending/processing. */
const REDACT_POLL_MS = 5000;

/** Wrap documentsApi.uploadWithScope so redact workbench stays matter-agnostic. */
export function useUploadDocumentForRedaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, title }: { file: File; title?: string }) =>
      documentsApi
        .uploadWithScope({
          file,
          title: title ?? file.name,
          type: "legal_doc",
          ownerScope: "personal",
        })
        .then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["workbench-documents"] });
    },
  });
}

/** POST /documents/{id}/redact — trigger async redaction. */
export function useTriggerRedaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, types }: { documentId: string; types: RedactType[] }) =>
      redactApi.trigger(documentId, types),
    onSuccess: (_, { documentId }) => {
      // resetQueries (not invalidateQueries) so a re-trigger after a prior
      // ready/failed run drops the stale report/error from cache immediately
      // — the UI falls back to the pending state instead of flashing the old
      // terminal result until the refetch resolves — and refetches the active
      // query, which restarts polling once the new status lands as
      // pending/processing.
      qc.resetQueries({ queryKey: ["redact-status", documentId] });
    },
  });
}

/**
 * GET /documents/{id}/redact-status — poll while pending/processing, stop on
 * terminal states (ready/failed) as well as the not-yet-triggered state
 * (status null/undefined) — otherwise a document that's uploaded but never
 * had redaction triggered would poll forever. Mirrors
 * useConversationAttachmentStatus.
 */
export function useRedactionStatus(documentId: string | null | undefined) {
  return useQuery({
    queryKey: ["redact-status", documentId ?? ""],
    queryFn: () => redactApi.status(documentId as string),
    enabled: !!documentId,
    refetchInterval: (query) => {
      const status = query.state.data?.redact_status;
      return status === "pending" || status === "processing" ? REDACT_POLL_MS : false;
    },
  });
}

// ---------------------------------------------------------------------------
// Scheduled Tasks (定时任务)
// ---------------------------------------------------------------------------

/** Paginated task list. */
export function useScheduledTasks(filters?: {
  status?: ScheduledTaskStatus;
  targetType?: ScheduledTaskTargetType;
  matterId?: string;
  instanceId?: string;
  q?: string;
  scope?: "mine" | "all";
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: queryKeys.scheduledTasks(filters as Record<string, unknown> | undefined),
    queryFn: () => scheduledTasksApi.list(filters),
  });
}

/** Single task detail. */
export function useScheduledTask(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.scheduledTask(id ?? ""),
    queryFn: () => scheduledTasksApi.get(id as string).then((res) => res.data),
    enabled: !!id,
  });
}

/** Execution history for one task, paginated, newest first. */
export function useScheduledTaskRuns(
  id: string | null | undefined,
  page?: number,
  pageSize?: number,
) {
  const params = { page, pageSize };
  return useQuery({
    queryKey: queryKeys.scheduledTaskRuns(id ?? "", params),
    queryFn: () => scheduledTasksApi.listRuns(id as string, params),
    enabled: !!id,
  });
}

/** Overview counts (active / paused / disabled / failedLast7Days) for the summary bar. */
export function useScheduledTaskSummary() {
  return useQuery({
    queryKey: queryKeys.scheduledTaskSummary,
    queryFn: () => scheduledTasksApi.summary().then((res) => res.data),
  });
}

export function useCreateScheduledTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ScheduledTaskCreatePayload) =>
      scheduledTasksApi.create(payload).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled-tasks"] });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTaskSummary });
    },
  });
}

export function useUpdateScheduledTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; data: ScheduledTaskUpdatePayload }) =>
      scheduledTasksApi.update(vars.id, vars.data).then((res) => res.data),
    onSuccess: (task, vars) => {
      qc.invalidateQueries({ queryKey: ["scheduled-tasks"] });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTaskSummary });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTask(vars.id) });
      qc.setQueryData(queryKeys.scheduledTask(vars.id), task);
    },
  });
}

export function useDeleteScheduledTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scheduledTasksApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled-tasks"] });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTaskSummary });
    },
  });
}

/** active → paused. 409 if the task is not currently active. */
export function usePauseScheduledTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scheduledTasksApi.pause(id).then((res) => res.data),
    onSuccess: (task, id) => {
      qc.invalidateQueries({ queryKey: ["scheduled-tasks"] });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTaskSummary });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTask(id) });
      qc.setQueryData(queryKeys.scheduledTask(id), task);
    },
  });
}

/** paused → active. 409 if the task is not currently paused. */
export function useResumeScheduledTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scheduledTasksApi.resume(id).then((res) => res.data),
    onSuccess: (task, id) => {
      qc.invalidateQueries({ queryKey: ["scheduled-tasks"] });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTaskSummary });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTask(id) });
      qc.setQueryData(queryKeys.scheduledTask(id), task);
    },
  });
}

/**
 * Fire the task immediately (trigger_kind=manual). Does not change
 * `nextRunAt`/`runCount` on the task, but does create a new run-history row,
 * so the runs list + summary (failedLast7Days) are invalidated alongside it.
 */
export function useRunScheduledTaskNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scheduledTasksApi.runNow(id).then((res) => res.data),
    onSuccess: (_run, id) => {
      qc.invalidateQueries({ queryKey: ["scheduled-tasks"] });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTaskSummary });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTask(id) });
      // Prefix-only (no pagination params) so it matches every cached page.
      qc.invalidateQueries({ queryKey: ["scheduled-tasks", "detail", id, "runs"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Scheduled run review — 期产物人审闭环（波次 2）
// ---------------------------------------------------------------------------

/** 期产物视图。accept/return/save-as-document/表态成功后都整体失效重读。 */
export function useScheduledRunReview(id: { taskId: string; runId: string } | null) {
  return useQuery({
    queryKey: queryKeys.scheduledTaskRunReview(id?.taskId ?? "", id?.runId ?? ""),
    queryFn: () =>
      scheduledTasksApi.runReview(id!.taskId, id!.runId).then((res) => res.data),
    enabled: !!id,
  });
}

/** 整期采纳。note 选填；成功后重读 review 与执行历史（留痕/状态）。 */
export function useAcceptScheduledRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string; runId: string; note?: string | null }) =>
      scheduledTasksApi
        .acceptRun(vars.taskId, vars.runId, { note: vars.note })
        .then((res) => res.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: queryKeys.scheduledTaskRunReview(vars.taskId, vars.runId),
      });
      qc.invalidateQueries({ queryKey: ["scheduled-tasks", "detail", vars.taskId, "runs"] });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTask(vars.taskId) });
    },
  });
}

/** 整期退回。reason 必填（组件层先行校验，服务端 422 兜底）。 */
export function useReturnScheduledRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string; runId: string; reason: string }) =>
      scheduledTasksApi
        .returnRun(vars.taskId, vars.runId, { reason: vars.reason })
        .then((res) => res.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: queryKeys.scheduledTaskRunReview(vars.taskId, vars.runId),
      });
      qc.invalidateQueries({ queryKey: ["scheduled-tasks", "detail", vars.taskId, "runs"] });
      qc.invalidateQueries({ queryKey: queryKeys.scheduledTask(vars.taskId) });
    },
  });
}

/**
 * 把本期产物另存为事项文档。返回直达动作参数（发起审查 / 与上一版比对），
 * 成功后另读 review——savedDocuments 卡片与上期对照随新载荷更新。
 */
export function useSaveScheduledRunAsDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      taskId: string;
      runId: string;
      documentName?: string;
    }) =>
      scheduledTasksApi
        .saveRunAsDocument(vars.taskId, vars.runId, {
          documentName: vars.documentName,
        })
        .then((res) => res.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: queryKeys.scheduledTaskRunReview(vars.taskId, vars.runId),
      });
    },
  });
}

/**
 * 期产物逐条表态（统一表态表 POST /ai-verdicts）。
 *
 * 表态值按契约是 agree/disagree 两值（无撤销）：改判即再写一条新行，读取侧
 * 只回每个 targetRef 的最新一条。成功后重读 review 载荷回填界面。
 */
export function useCreateAiVerdict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: AiVerdictCreatePayload & { taskId: string; runId: string }) => {
      const { taskId: _taskId, runId: _runId, ...payload } = vars;
      return aiVerdictsApi.create(payload).then((res) => res.data);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.aiVerdicts });
      qc.invalidateQueries({
        queryKey: queryKeys.scheduledTaskRunReview(vars.taskId, vars.runId),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// AI Review Batches — 批量 AI 审查（波次 2）
// ---------------------------------------------------------------------------

/** 批仍在推进（批级 pending/running 或有行 pending/running）时 5 s 轮询。 */
function isBatchActive(batch: AiReviewBatch | undefined): boolean {
  if (!batch) return false;
  if (batch.status === "pending" || batch.status === "running") return true;
  return (batch.items ?? []).some(
    (item) => item.status === "pending" || item.status === "running",
  );
}

/** 批详情（进度条 + 行级表格数据源）。未终态时自动轮询。 */
export function useAiReviewBatch(id: string | null) {
  return useQuery({
    queryKey: queryKeys.aiReviewBatch(id ?? ""),
    queryFn: () => aiReviewBatchesApi.get(id as string).then((res) => res.data),
    enabled: !!id,
    refetchInterval: (query) => (isBatchActive(query.state.data) ? 5_000 : false),
  });
}

/** 批列表（文档库「批量审查记录」抽屉）。 */
export function useAiReviewBatches(params?: { matterId?: string; page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: queryKeys.aiReviewBatches(params as Record<string, unknown> | undefined),
    queryFn: () => aiReviewBatchesApi.list(params),
  });
}

/** 发起一批批量审查（202 受理，只回批 id，由调用方打开批详情抽屉）。 */
export function useCreateAiReviewBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { documentIds: string[]; stance?: string | null }) =>
      aiReviewBatchesApi.create(vars).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-review-batches"] });
    },
  });
}

/** 重试一个失败行；成功后批详情与批列表都重读。 */
export function useRetryAiReviewBatchItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { batchId: string; itemId: string }) =>
      aiReviewBatchesApi
        .retryItem(vars.batchId, vars.itemId)
        .then((res) => res.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["ai-review-batches"] });
    },
  });
}

// ---------------------------------------------------------------------------
// IPO Vault (境内外 IPO 宝库)
// ---------------------------------------------------------------------------

const IPO_VAULT_KEY = ["ipo-vault"] as const;

/** Overview statistics and metadata. */
export function useIpoOverview() {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "overview"],
    queryFn: () => ipoVaultApi.overview().then((res) => res.data),
  });
}

/** List legal issues with filters. */
export function useIpoLegalIssues(params?: LegalIssuesParams) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "legal-issues", params],
    queryFn: () => ipoVaultApi.legalIssues(params),
  });
}

/** Get legal issue facets for filter options. */
export function useIpoLegalIssueFacets(params?: Pick<LegalIssuesParams, "market" | "board" | "issueYear" | "projectStatus">) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "legal-issues", "facets", params],
    queryFn: () => ipoVaultApi.legalIssueFacets(params).then((res) => res.data),
  });
}

/** Get single legal issue detail. */
export function useIpoLegalIssue(uid: string) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "legal-issues", uid],
    queryFn: () => ipoVaultApi.legalIssue(uid).then((res) => res.data),
    enabled: !!uid,
  });
}

/** List CSRC filings by category. */
export function useIpoCsrcFilings(category: string, params?: CsrcFilingsParams) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "csrc", "filings", category, params],
    queryFn: () => ipoVaultApi.csrcFilings(category, params),
    enabled: !!category,
  });
}

/** List CSRC feedback with filters. */
export function useIpoCsrcFeedback(params?: CsrcFeedbackParams) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "csrc", "feedback", params],
    queryFn: () => ipoVaultApi.csrcFeedback(params),
  });
}

/** Get CSRC feedback facets. */
export function useIpoCsrcFeedbackFacets() {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "csrc", "feedback", "facets"],
    queryFn: () => ipoVaultApi.csrcFeedbackFacets().then((res) => res.data),
  });
}

/** Get filing timeline snapshot. */
export function useIpoCsrcTimeline() {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "csrc", "timeline"],
    queryFn: () => ipoVaultApi.csrcTimeline().then((res) => res.data),
  });
}

/** List HKEX new listing cases by category. */
export function useIpoHkexCases(category: string, params?: HkexCasesParams) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "hkex", "cases", category, params],
    queryFn: () => ipoVaultApi.hkexCases(category, params),
    enabled: !!category,
  });
}

/** List HKEX announcements with filters. */
export function useIpoHkexAnnouncements(params?: HkexAnnouncementsParams) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "hkex", "announcements", params],
    queryFn: () => ipoVaultApi.hkexAnnouncements(params),
  });
}

/** List SEC filings by form type. */
export function useIpoSecFilings(formType: string, params?: SecFilingsParams) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "sec", formType, params],
    queryFn: () => ipoVaultApi.sec(formType, params),
    enabled: !!formType,
  });
}

/** List sync run history. */
export function useIpoSyncRuns(params?: SyncRunsParams) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "sync", "runs", params],
    queryFn: () => ipoVaultApi.syncRuns(params),
  });
}

/** Trigger incremental sync. */
export function useTriggerIpoSync() {
  return useMutation({
    mutationFn: () => ipoVaultApi.triggerSync().then((res) => res.data),
  });
}

/** Trigger full backfill. */
export function useTriggerIpoBackfill() {
  return useMutation({
    mutationFn: () => ipoVaultApi.triggerBackfill().then((res) => res.data),
  });
}

// ---------------------------------------------------------------------------
// Insights (Professional Intelligence Layer)
// ---------------------------------------------------------------------------

/** Get audit trends by board and months. */
export function useIpoInsightsTrends(months: number) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "insights", "trends", months],
    queryFn: () => ipoVaultApi.insightsTrends(months),
  });
}

/** Get inquiry hotspots by months, optional market/board filter. */
export function useIpoInsightsHotspots(months: number, market?: string, board?: string) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "insights", "hotspots", months, market, board],
    queryFn: () => ipoVaultApi.insightsHotspots(months, market, board),
  });
}

/** Get board profile by market, board, and months. */
export function useIpoBoardProfile(market: string, board?: string, months?: number) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "insights", "board-profile", { market, board, months }],
    queryFn: () => ipoVaultApi.insightsBoardProfile(market, board, months),
    enabled: !!market && market.length > 0,
  });
}

/** Get board benchmarks. */
export function useIpoInsightsBenchmarks() {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "insights", "benchmarks"],
    queryFn: () => ipoVaultApi.insightsBenchmarks(),
  });
}

// ---------------------------------------------------------------------------
// Company Search & Detail
// ---------------------------------------------------------------------------

/** Search companies by name or keyword. */
export function useIpoCompanySearch(q: string, limit = 10) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "companies", "search", q, limit],
    queryFn: () => ipoVaultApi.companySearch(q, limit),
    enabled: q.length > 0,
  });
}

/** Get company detail by name. */
export function useIpoCompanyDetail(name: string) {
  return useQuery({
    queryKey: [...IPO_VAULT_KEY, "companies", "detail", name],
    queryFn: () => ipoVaultApi.companyDetail(name).then((res) => res.data),
    enabled: !!name,
  });
}

// ---------------------------------------------------------------------------
// IPO Vault QA (宝库 AI 问答，期 4)
// ---------------------------------------------------------------------------

const IPO_VAULT_QA_KEY = ["ipo-vault-qa"] as const;

/** 本人宝库问答会话列表（历史侧栏数据源）。 */
export function useVaultQaSessions() {
  return useQuery<VaultQaSessionSummary[]>({
    queryKey: [...IPO_VAULT_QA_KEY, "sessions"],
    queryFn: () => vaultQaApi.sessions(),
  });
}

/** 单个问答会话的全部轮次（只读回放）。 */
export function useVaultQaSessionRuns(sessionId: string | null) {
  return useQuery<VaultQaRunRecord[]>({
    queryKey: [...IPO_VAULT_QA_KEY, "sessions", sessionId, "runs"],
    queryFn: () => vaultQaApi.sessionRuns(sessionId as string),
    enabled: !!sessionId,
  });
}
